import React, { useState, useEffect, useRef } from 'react';
import { supabase, SUPABASE_CONFIGURED } from './supabase.js';

// ============================================================
// 定数
// ============================================================
const TIFFANY = '#0ABAB5';
const TIFFANY_DARK = '#089B96';
const ADMIN_PW = 'AceKing@Fukuoka0425';

const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const DAY_COLORS = ['#E8536C', '#3D3D3D', '#3D3D3D', '#3D3D3D', '#3D3D3D', '#3D3D3D', '#3B82F6'];
const SUNDAY_RED = '#E8536C';
const SATURDAY_BLUE = '#3B82F6';

const JP_HOLIDAYS = new Set([
  '2026-01-01', '2026-01-12', '2026-02-11', '2026-02-23',
  '2026-03-20', '2026-04-29', '2026-05-03', '2026-05-04',
  '2026-05-05', '2026-05-06', '2026-07-20', '2026-08-11',
  '2026-09-21', '2026-09-22', '2026-09-23', '2026-10-12',
  '2026-11-03', '2026-11-23',
  '2027-01-01', '2027-01-11', '2027-02-11', '2027-02-23',
  '2027-03-21', '2027-03-22', '2027-04-29', '2027-05-03',
  '2027-05-04', '2027-05-05', '2027-07-19', '2027-08-11',
  '2027-09-20', '2027-09-23', '2027-10-11', '2027-11-03',
  '2027-11-23',
]);

// ============================================================
// ヘルパー関数
// ============================================================
function haptic(type = 'light') {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  const patterns = {
    light: 8, medium: 15,
    success: [20, 40, 30], error: [50, 60, 50], warning: [30, 30, 30],
  };
  try { navigator.vibrate(patterns[type] || 10); } catch (e) {}
}

function isHoliday(dateStr) { return JP_HOLIDAYS.has(dateStr); }

// ローカルタイムゾーンで YYYY-MM-DD 形式の日付文字列を返す
// new Date().toISOString() を使うと UTC で計算されて1日ズレることがあるため
function toLocalDateStr(d) {
  if (!d) d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDateColor(dateStr) {
  if (isHoliday(dateStr)) return SUNDAY_RED;
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  if (dow === 0) return SUNDAY_RED;
  if (dow === 6) return SATURDAY_BLUE;
  return '#3D3D3D';
}

function formatDateFull(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return `${d.getMonth() + 1}月${d.getDate()}日 (${DAY_LABELS[d.getDay()]})`;
}

function compressImage(file, maxSize = 1024, quality = 0.85) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round(height * maxSize / width);
              width = maxSize;
            } else {
              width = Math.round(width * maxSize / height);
              height = maxSize;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}

// ============================================================
// SVGアイコン (lucide-react依存なし)
// ============================================================
const Icon = ({ children, size = 20, color = 'currentColor', strokeWidth = 2, fill = 'none', style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
    {children}
  </svg>
);
const IconCalendar = (p) => <Icon {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Icon>;
const IconUsers = (p) => <Icon {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const IconClock = (p) => <Icon {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Icon>;
const IconStar = (p) => <Icon {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></Icon>;
const IconCheck = (p) => <Icon {...p}><polyline points="20 6 9 17 4 12"/></Icon>;
const IconX = (p) => <Icon {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Icon>;
const IconChevronLeft = (p) => <Icon {...p}><polyline points="15 18 9 12 15 6"/></Icon>;
const IconChevronRight = (p) => <Icon {...p}><polyline points="9 18 15 12 9 6"/></Icon>;
const IconPlus = (p) => <Icon {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Icon>;
const IconTrash = (p) => <Icon {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></Icon>;
const IconEdit = (p) => <Icon {...p}><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></Icon>;
const IconImage = (p) => <Icon {...p}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></Icon>;
const IconSettings = (p) => <Icon {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></Icon>;
const IconShield = (p) => <Icon {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Icon>;
const IconSearch = (p) => <Icon {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Icon>;

// ============================================================
// ロゴ画像 (base64)
// ============================================================
const LOGO = "data:image/png;base64,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";
// 注: 上記のロゴデータは元のApp.jsxからそのまま引き継いでいます。前のバージョンと同じです。


// ============================================================
// ErrorBoundary
// ============================================================
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, fontFamily: 'monospace', background: '#fff', minHeight: '100vh' }}>
          <h2 style={{ color: '#e11d48' }}>エラーが発生しました</h2>
          <pre style={{ background: '#fef2f2', padding: 12, borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {String(this.state.error)}
            {this.state.error && this.state.error.stack ? ('\n\n' + this.state.error.stack) : ''}
          </pre>
          <button onClick={() => { try { localStorage.clear(); } catch(e){} location.reload(); }} style={{ padding: '8px 16px', background: TIFFANY, color: 'white', border: 'none', borderRadius: 8, marginTop: 12, cursor: 'pointer' }}>
            データをクリアして再読み込み
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================
// スプラッシュ画面
// ============================================================
function SplashScreen({ onFinish }) {
  const [phase, setPhase] = useState('intro');
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('outro'), 3000);
    const t2 = setTimeout(() => { if (onFinishRef.current) onFinishRef.current(); }, 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div
      onClick={() => onFinishRef.current && onFinishRef.current()}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle at center, #1FCFC9 0%, #0ABAB5 50%, #089B96 100%)',
        cursor: 'pointer', overflow: 'hidden',
        opacity: phase === 'outro' ? 0 : 1,
        transition: 'opacity 1.5s ease-out',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.15) 0%, transparent 50%)',
        animation: 'splashShimmer 4s ease-in-out infinite',
      }} />
      <div style={{
        position: 'relative', zIndex: 2,
        background: 'rgba(255,255,255,0.95)',
        padding: 32, borderRadius: 32,
        boxShadow: '0 30px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.5)',
        animation: 'splashIn 1.4s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}>
        <img src={LOGO} alt="MUSASHI POKER ROOM" style={{
          width: 180, height: 180, objectFit: 'contain', display: 'block',
        }} />
      </div>
      <div style={{
        position: 'absolute', width: 280, height: 280,
        border: '2px solid rgba(255,255,255,0.4)', borderRadius: '50%',
        animation: 'splashRing 2.4s ease-out 0.4s infinite',
      }} />
      <div style={{
        position: 'absolute', bottom: '15%', left: 0, right: 0,
        textAlign: 'center', color: 'white',
        fontSize: 11, letterSpacing: '0.4em', fontWeight: 300,
        animation: 'splashIn 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.6s both',
      }}>
        WELCOME TO<br/>
        <span style={{ fontSize: 14, fontWeight: 'bold', letterSpacing: '0.3em', marginTop: 4, display: 'inline-block' }}>
          MUSASHI POKER ROOM
        </span>
      </div>
      <style>{`
        @keyframes splashIn {
          0% { opacity: 0; transform: scale(0.5) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes splashRing {
          0% { opacity: 1; transform: scale(0.6); }
          100% { opacity: 0; transform: scale(1.6); }
        }
        @keyframes splashShimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}


// ============================================================
// 共通スタイル
// ============================================================
const sty = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F4F9FC 0%, #DDECF6 50%, #C9DFEE 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Hiragino Sans", "Yu Gothic", sans-serif',
    paddingBottom: 80,
  },
  card: {
    background: 'white', borderRadius: 16, padding: 16, marginBottom: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid rgba(255,255,255,0.6)',
  },
  cardLg: {
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)',
    borderRadius: 20, padding: 24, marginBottom: 16,
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
    border: '1px solid rgba(255,255,255,0.6)',
  },
  input: {
    width: '100%', padding: '12px 16px', fontSize: 16,
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
    outline: 'none', boxSizing: 'border-box',
  },
  btn: {
    width: '100%', padding: '14px 16px', fontSize: 16, fontWeight: 'bold',
    background: TIFFANY, color: 'white', border: 'none', borderRadius: 10,
    cursor: 'pointer', transition: 'all 0.2s',
  },
  btnSec: {
    padding: '8px 16px', fontSize: 14,
    background: 'white', color: '#475569', border: '1px solid #cbd5e1',
    borderRadius: 8, cursor: 'pointer',
  },
  btnGhost: {
    padding: '8px 12px', fontSize: 14,
    background: 'transparent', color: '#64748b', border: 'none',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  label: { fontSize: 12, color: '#64748b', marginBottom: 4, display: 'block' },
  required: { color: '#e11d48', marginLeft: 2 },
  header: {
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
    position: 'sticky', top: 0, zIndex: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
};

// ============================================================
// クリッカブルロゴ (NEW: ロゴクリックでホームへ)
// ============================================================
function ClickableLogo({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: 0, display: 'flex', alignItems: 'center',
      }}
      aria-label="ホームへ戻る"
    >
      <img src={LOGO} alt="MUSASHI POKER ROOM"
        style={{ height: 44, transition: 'transform 0.2s' }}
        onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.95)'; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.95)'; }}
        onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      />
    </button>
  );
}

// ============================================================
// ナビゲーションボタン
// ============================================================
function NavBtn({ active, icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: '8px 4px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        color: active ? TIFFANY : '#94a3b8',
        fontWeight: active ? 'bold' : 'normal',
      }}
    >
      {icon}
      <div style={{ fontSize: 10 }}>{label}</div>
    </button>
  );
}

// ============================================================
// DateTabs
// ============================================================
function DateTabs({ activeKey, onChange }) {
  const containerRef = useRef(null);
  const tabRefs = useRef({});
  const userScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef(null);
  const lastAutoScrollKeyRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tabs = [{ key: 'recommend', recommend: true }];
  for (let i = 0; i < 21; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const key = toLocalDateStr(d);
    tabs.push({
      key, month: d.getMonth() + 1, day: d.getDate(),
      dayLabel: DAY_LABELS[d.getDay()], dayColor: getDateColor(key),
    });
  }

  // ユーザーの手動スクロールを検知
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    function onTouchStart() {
      userScrollingRef.current = true;
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
        userScrollTimeoutRef.current = null;
      }
    }
    function onTouchEnd() {
      // タッチ終了後しばらくは「ユーザー操作中」とみなす
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current);
      userScrollTimeoutRef.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, 800);
    }
    function onWheel() {
      // PCのマウスホイール/トラックパッドにも対応
      userScrollingRef.current = true;
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current);
      userScrollTimeoutRef.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, 800);
    }

    c.addEventListener('touchstart', onTouchStart, { passive: true });
    c.addEventListener('touchend', onTouchEnd, { passive: true });
    c.addEventListener('touchcancel', onTouchEnd, { passive: true });
    c.addEventListener('wheel', onWheel, { passive: true });

    return () => {
      c.removeEventListener('touchstart', onTouchStart);
      c.removeEventListener('touchend', onTouchEnd);
      c.removeEventListener('touchcancel', onTouchEnd);
      c.removeEventListener('wheel', onWheel);
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current);
    };
  }, []);

  // activeKeyが変わったら、その日付タブを中央へ自動スクロール
  // ただし、ユーザーが手動でスクロール中はスキップ
  useEffect(() => {
    // 手動スクロール中はスキップ
    if (userScrollingRef.current) return;
    // 同じキーへの重複実行を防ぐ
    if (lastAutoScrollKeyRef.current === activeKey) return;
    lastAutoScrollKeyRef.current = activeKey;

    try {
      const c = containerRef.current;
      const a = tabRefs.current[activeKey];
      if (!c || !a) return;
      const cr = c.getBoundingClientRect();
      const ar = a.getBoundingClientRect();
      const target = ar.left - cr.left + c.scrollLeft + ar.width / 2 - cr.width / 2;
      if (c.scrollTo) c.scrollTo({ left: target, behavior: 'smooth' });
      else c.scrollLeft = target;
    } catch (e) { console.warn('tab scroll', e); }
  }, [activeKey]);

  return (
    <div
      ref={containerRef}
      data-date-tabs="true"
      style={{
        display: 'flex', overflowX: 'auto', gap: 4, padding: '8px 12px',
        background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)',
        WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
        position: 'sticky', top: 64, zIndex: 9,
        borderBottom: '1px solid rgba(0,0,0,0.04)',
      }}
    >
      {tabs.map((tab) => {
        const active = activeKey === tab.key;
        return (
          <button
            key={tab.key}
            ref={(el) => { tabRefs.current[tab.key] = el; }}
            onClick={() => { haptic('light'); onChange(tab.key); }}
            style={{
              minWidth: 60, padding: '8px 10px', flexShrink: 0,
              background: active ? 'rgba(10, 186, 181, 0.08)' : 'transparent',
              border: 'none', borderRadius: 12, cursor: 'pointer',
              position: 'relative',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}
          >
            {tab.recommend ? (
              <>
                <div style={{ fontSize: 18, lineHeight: 1 }}>
                  <IconStar size={18} color={active ? TIFFANY : '#94a3b8'} fill={active ? TIFFANY : 'none'} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 'bold', color: active ? TIFFANY : '#64748b', marginTop: 2 }}>オススメ</div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <span style={{ fontSize: 10, color: active ? TIFFANY : '#94a3b8' }}>{tab.month}/</span>
                  <span style={{ fontSize: 20, fontWeight: 'bold', color: active ? TIFFANY : '#475569' }}>{tab.day}</span>
                </div>
                <div style={{ fontSize: 10, color: tab.dayColor, marginTop: 2, fontWeight: 'bold' }}>
                  {tab.dayLabel}
                </div>
              </>
            )}
            {active && (
              <div style={{
                position: 'absolute', bottom: -8, left: '20%', right: '20%',
                height: 3, background: TIFFANY, borderRadius: 2,
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// HoursBanner
// ============================================================
function HoursBanner({ date, hours }) {
  if (!date) return null;
  const h = hours || {};
  const closed = h.closed === true;
  return (
    <div style={{
      margin: '12px 16px', padding: '10px 14px', borderRadius: 12,
      background: closed ? 'rgba(239,68,68,0.08)' : 'rgba(10,186,181,0.06)',
      border: `1px solid ${closed ? 'rgba(239,68,68,0.2)' : 'rgba(10,186,181,0.15)'}`,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <IconClock size={16} color={closed ? '#dc2626' : TIFFANY} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#64748b' }}>営業時間</div>
        <div style={{ fontSize: 14, fontWeight: 'bold', color: closed ? '#dc2626' : '#0f766e' }}>
          {closed ? '定休日' : (h.open_time && h.close_time ? `${h.open_time} 〜 ${h.close_time}` : '11:00 〜 23:00')}
        </div>
      </div>
      {h.note && <div style={{ fontSize: 11, color: '#64748b', maxWidth: 180, textAlign: 'right' }}>{h.note}</div>}
    </div>
  );
}


// ============================================================
// Field (ラベル付き入力)
// ============================================================
function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={sty.label}>
        {label}{required && <span style={sty.required}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ============================================================
// Row (情報の行)
// ============================================================
function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>{value}</div>
    </div>
  );
}

// ============================================================
// 日付ヘッダ
// ============================================================
function DateHeader({ dateStr }) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return null;
  const color = getDateColor(dateStr);
  const isHol = isHoliday(dateStr);
  return (
    <div style={{
      margin: '16px 16px 8px', display: 'flex', alignItems: 'baseline', gap: 8,
    }}>
      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b' }}>
        {d.getMonth() + 1}/{d.getDate()}
      </div>
      <div style={{ fontSize: 14, color, fontWeight: 'bold' }}>
        ({DAY_LABELS[d.getDay()]}{isHol ? '・祝' : ''})
      </div>
    </div>
  );
}

// ============================================================
// 枠データを日付別にグループ化
// ============================================================
function groupByDate(slots) {
  const groups = {};
  for (const s of slots) {
    if (!s || !s.date) continue;
    if (!groups[s.date]) groups[s.date] = [];
    groups[s.date].push(s);
  }
  for (const date in groups) {
    groups[date].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }
  return groups;
}

// ============================================================
// AdCarousel (広告カルーセル - 複数枚スワイプ対応)
// ============================================================
function AdCarousel({ ads, onAdClick }) {
  const [idx, setIdx] = useState(0);
  const [maxRatio, setMaxRatio] = useState(0.5); // height/width の最大比率
  const touchStartRef = useRef(null);
  const intervalRef = useRef(null);

  // ads は文字列または {data_url, slot_ids} オブジェクトの配列
  // 内部では常にオブジェクト形式で扱う
  const validAds = (ads || [])
    .filter(Boolean)
    .map((a) => {
      if (typeof a === 'string') return { data_url: a, slot_ids: [] };
      return { data_url: a.data_url, slot_ids: Array.isArray(a.slot_ids) ? a.slot_ids : [] };
    })
    .filter((a) => a.data_url);
  const count = validAds.length;

  // 全画像の縦横比を取得して、一番縦長のものに合わせて枠の高さを決める
  useEffect(() => {
    if (count === 0) return;
    let cancelled = false;
    let maxR = 0;
    let loaded = 0;
    validAds.forEach((ad) => {
      const img = new window.Image();
      img.onload = () => {
        if (cancelled) return;
        if (img.naturalWidth > 0) {
          const r = img.naturalHeight / img.naturalWidth;
          if (r > maxR) maxR = r;
        }
        loaded++;
        if (loaded === count) {
          const finalRatio = Math.min(Math.max(maxR, 0.4), 1.2);
          setMaxRatio(finalRatio);
        }
      };
      img.onerror = () => {
        loaded++;
        if (loaded === count) {
          setMaxRatio(Math.min(Math.max(maxR, 0.4), 1.2));
        }
      };
      img.src = ad.data_url;
    });
    return () => { cancelled = true; };
  }, [count, validAds.map(a => a.data_url).join('|').slice(0, 100)]);

  // 自動切替（複数枚ある場合のみ、5秒ごと）
  useEffect(() => {
    if (count < 2) return;
    intervalRef.current = setInterval(() => {
      setIdx((prev) => (prev + 1) % count);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [count]);

  // ユーザー操作時、自動切替を一時停止
  function pauseAutoplay() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // 10秒後に再開
    setTimeout(() => {
      if (count < 2) return;
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          setIdx((prev) => (prev + 1) % count);
        }, 5000);
      }
    }, 10000);
  }

  function handleTouchStart(e) {
    if (!e.touches || !e.touches[0]) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
  }

  function handleTouchEnd(e) {
    const start = touchStartRef.current;
    if (!start || !e.changedTouches || !e.changedTouches[0]) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    touchStartRef.current = null;
    if (Math.abs(dx) < 50) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dt > 1000) return;
    if (count < 2) return;
    pauseAutoplay();
    if (dx < 0) {
      haptic('light');
      setIdx((prev) => (prev + 1) % count);
    } else {
      haptic('light');
      setIdx((prev) => (prev - 1 + count) % count);
    }
  }

  if (count === 0) return null;

  return (
    <div data-ad-carousel="true" style={{ padding: '12px 16px 0' }}>
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          background: '#f1f5f9',
          // 全画像で共通の枠サイズを保つ（縦横比固定）
          aspectRatio: `1 / ${maxRatio}`,
        }}
      >
        {/* スライドコンテナ */}
        <div style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(-${idx * 100}%)`,
        }}>
          {validAds.map((ad, i) => {
            const hasLink = Array.isArray(ad.slot_ids) && ad.slot_ids.length > 0;
            return (
              <div
                key={i}
                onClick={() => {
                  if (hasLink && onAdClick) {
                    haptic('light');
                    onAdClick(ad);
                  }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f1f5f9',
                  position: 'relative',
                  cursor: hasLink ? 'pointer' : 'default',
                }}
              >
                <img
                  src={ad.data_url}
                  alt=""
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                  draggable={false}
                />
                {hasLink && (
                  <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(10, 186, 181, 0.95)',
                    color: 'white',
                    fontSize: 10,
                    fontWeight: 'bold',
                    padding: '4px 8px',
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    pointerEvents: 'none',
                  }}>
                    タップで予約 →
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* インジケータドット */}
        {count > 1 && (
          <div style={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 5,
            pointerEvents: 'none',
          }}>
            {validAds.map((_, i) => (
              <div key={i} style={{
                width: i === idx ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: i === idx ? 'white' : 'rgba(255,255,255,0.6)',
                transition: 'all 0.3s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }} />
            ))}
          </div>
        )}

        {/* 左右のボタン (PCでも操作できるよう) */}
        {count > 1 && (
          <>
            <button
              onClick={() => { haptic('light'); pauseAutoplay(); setIdx((prev) => (prev - 1 + count) % count); }}
              style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.8)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                opacity: 0.7,
              }}
            >
              <IconChevronLeft size={16} color="#1e293b" />
            </button>
            <button
              onClick={() => { haptic('light'); pauseAutoplay(); setIdx((prev) => (prev + 1) % count); }}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.8)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                opacity: 0.7,
              }}
            >
              <IconChevronRight size={16} color="#1e293b" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// AdSlotsModal (広告タップ時の関連枠一覧モーダル - NEW!)
// ============================================================
function AdSlotsModal({ ad, slots, reservations, onClose, onSelectSlot }) {
  if (!ad) return null;

  const slotIds = Array.isArray(ad.slot_ids) ? ad.slot_ids : [];
  const slotMap = {};
  for (const s of (slots || [])) { if (s && s.id) slotMap[s.id] = s; }

  // 関連枠を取得（過去のものは除外）& 日付順にソート
  const todayStr = toLocalDateStr();
  const linkedSlots = slotIds
    .map((id) => slotMap[id])
    .filter((s) => s && s.date && s.date >= todayStr)
    .sort((a, b) => ((a.date || '') + (a.time || '')).localeCompare((b.date || '') + (b.time || '')));

  const reservationsBySlot = {};
  for (const r of (reservations || [])) {
    if (r && r.slot_id) {
      reservationsBySlot[r.slot_id] = (reservationsBySlot[r.slot_id] || 0) + 1;
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, maxHeight: '85vh',
          background: 'white', borderRadius: '20px 20px 0 0',
          padding: '20px 16px',
          overflowY: 'auto',
          animation: 'slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* ハンドル */}
        <div style={{
          width: 40, height: 4, background: '#cbd5e1', borderRadius: 2,
          margin: '0 auto 16px',
        }} />

        {/* ヘッダー */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold' }}>
              FEATURED EVENT
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 'bold', color: '#1e293b', margin: '2px 0 0' }}>
              対象イベント一覧
            </h2>
          </div>
          <button onClick={onClose} style={{
            background: '#f1f5f9', border: 'none', borderRadius: '50%',
            width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconX size={18} color="#64748b" />
          </button>
        </div>

        {/* 広告サムネイル */}
        <div style={{
          width: '100%', maxHeight: 160, marginBottom: 16,
          borderRadius: 10, overflow: 'hidden',
          background: '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src={ad.data_url} alt="" style={{
            maxWidth: '100%', maxHeight: 160,
            objectFit: 'contain', display: 'block',
          }} />
        </div>

        {/* 枠一覧 */}
        {linkedSlots.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>
            <IconCalendar size={28} color="#cbd5e1" />
            <div style={{ marginTop: 8, fontSize: 13 }}>
              対象のイベントがありません<br/>
              <span style={{ fontSize: 11, color: '#cbd5e1' }}>(終了済み or 削除済み)</span>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              {linkedSlots.length}件のイベントが対象です。タップで予約画面へ進みます。
            </div>
            {linkedSlots.map((slot) => {
              const cap = slot.capacity || 0;
              const dummy = slot.dummy_reserved || 0;
              const totalReserved = (reservationsBySlot[slot.id] || 0) + dummy;
              const remaining = Math.max(0, cap - totalReserved);
              const isFull = remaining <= 0;
              return (
                <div
                  key={slot.id}
                  onClick={() => {
                    if (isFull) return;
                    haptic('medium');
                    onSelectSlot(slot);
                  }}
                  style={{
                    ...sty.card,
                    borderLeft: `4px solid ${isFull ? '#cbd5e1' : TIFFANY}`,
                    cursor: isFull ? 'not-allowed' : 'pointer',
                    opacity: isFull ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {slot.recommended && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 9, fontWeight: 'bold', color: '#92400e',
                          background: '#fef3c7', padding: '2px 5px', borderRadius: 3,
                          marginBottom: 4,
                        }}>
                          <IconStar size={9} color="#92400e" fill="#92400e" />オススメ
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 1 }}>
                        {slot.title || ''}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b' }}>
                        {formatDateFull(slot.date)} {slot.time}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <div style={{ fontSize: 9, color: '#94a3b8' }}>残り</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: isFull ? '#94a3b8' : TIFFANY, lineHeight: 1 }}>
                        {remaining}
                        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 'normal' }}>/{cap}</span>
                      </div>
                    </div>
                    <IconChevronRight size={16} color={isFull ? '#cbd5e1' : TIFFANY} />
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// SlotCard (予約枠カード)
// ============================================================
function SlotCard({ slot, reserved, alreadyReserved, onSelect, onShowDetail }) {
  if (!slot) return null;
  const hasDetail = !!(slot.description || (Array.isArray(slot.images) && slot.images.length > 0));
  const cap = slot.capacity || 0;
  const dummy = slot.dummy_reserved || 0;
  const totalReserved = reserved + dummy;
  const remaining = Math.max(0, cap - totalReserved);
  const isFull = remaining <= 0;
  const pct = cap > 0 ? Math.min(100, (totalReserved / cap) * 100) : 0;

  let progressColor = TIFFANY;
  if (pct >= 90) progressColor = '#dc2626';
  else if (pct >= 70) progressColor = '#f59e0b';

  let statusText;
  if (isFull) statusText = '満席';
  else if (pct >= 90) statusText = '残りわずか';
  else if (pct >= 70) statusText = '混雑';
  else if (totalReserved === 0) statusText = '空席あり';
  else statusText = `空き ${remaining}席`;

  const canClick = !isFull && !alreadyReserved;

  return (
    <div
      style={{
        ...sty.card,
        borderLeft: `4px solid ${
          alreadyReserved ? '#fbbf24' : (isFull ? '#cbd5e1' : TIFFANY)
        }`,
        opacity: (isFull && !alreadyReserved) ? 0.6 : 1,
        cursor: 'default',
        transition: 'transform 0.1s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {alreadyReserved && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 'bold', color: '#92400e',
              background: '#fef3c7', padding: '2px 6px', borderRadius: 4,
              marginBottom: 6, marginRight: 4,
            }}>
              ✓ 予約済み
            </div>
          )}
          {slot.recommended && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 'bold', color: '#92400e',
              background: '#fef3c7', padding: '2px 6px', borderRadius: 4,
              marginBottom: 6,
            }}>
              <IconStar size={10} color="#92400e" fill="#92400e" />
              オススメ
            </div>
          )}
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{slot.title || ''}</div>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', lineHeight: 1.2 }}>
            {slot.time || ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>残り</div>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: isFull ? '#94a3b8' : TIFFANY, lineHeight: 1 }}>
            {remaining}
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 'normal' }}>/{cap}</span>
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 10, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', background: progressColor,
          transition: 'width 0.3s', borderRadius: 3,
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
        <span>予約 {totalReserved}/{cap}名</span>
        <span style={{ color: pct >= 90 ? '#dc2626' : (pct >= 70 ? '#f59e0b' : '#64748b') }}>{statusText}</span>
      </div>

      {hasDetail && onShowDetail && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            haptic('light');
            onShowDetail();
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            marginTop: 10, fontSize: 12, color: TIFFANY,
            cursor: 'pointer', fontWeight: 500,
            borderBottom: `1px dashed ${TIFFANY}`,
            paddingBottom: 1,
          }}
        >
          📋 詳しく見る <IconChevronRight size={12} color={TIFFANY} />
        </div>
      )}

      <button
        disabled={!canClick}
        onClick={(e) => { e.stopPropagation(); if (canClick) { haptic('medium'); onSelect(); } }}
        style={{
          width: '100%', marginTop: 12, padding: '10px',
          background: alreadyReserved ? '#fef3c7' : (isFull ? '#f1f5f9' : TIFFANY),
          color: alreadyReserved ? '#92400e' : (isFull ? '#94a3b8' : 'white'),
          border: 'none', borderRadius: 999,
          fontSize: 14, fontWeight: 'bold',
          cursor: canClick ? 'pointer' : 'not-allowed',
        }}
      >
        {alreadyReserved ? '✓ 予約済み' : (isFull ? '満席' : '予約する')}
      </button>
    </div>
  );
}


// ============================================================
// LoginView (Supabase認証版)
// ============================================================
function LoginView({ onSignupClick, onResetClick }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function handleLogin() {
    setErrorMsg('');
    if (!email.trim() || !password) {
      haptic('error');
      setErrorMsg('メールアドレスとパスワードを入力してください');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        haptic('error');
        if (error.message.includes('Invalid')) {
          setErrorMsg('メールアドレスまたはパスワードが違います');
        } else if (error.message.includes('Email not confirmed')) {
          setErrorMsg('メール認証がまだ完了していません。受信メールをご確認ください');
        } else {
          setErrorMsg(error.message);
        }
        return;
      }
      haptic('success');
    } catch (e) {
      console.error(e);
      haptic('error');
      setErrorMsg('ログインに失敗しました');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ padding: '32px 16px', maxWidth: 420, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src={LOGO} alt="" style={{ width: 80, height: 80 }} />
        <div style={{ fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold', marginTop: 8 }}>
          MEMBER LOGIN
        </div>
      </div>
      <div style={sty.cardLg}>
        {errorMsg && (
          <div style={{
            padding: 12, marginBottom: 12, background: '#fef2f2',
            border: '1px solid #fca5a5', borderRadius: 8,
            color: '#b91c1c', fontSize: 13,
          }}>
            {errorMsg}
          </div>
        )}
        <Field label="メールアドレス" required>
          <input type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@example.com" style={sty.input}
            autoCapitalize="off" autoComplete="email" />
        </Field>
        <Field label="パスワード" required>
          <input type="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            placeholder="••••••••" style={sty.input}
            autoComplete="current-password" />
        </Field>
        <button onClick={handleLogin} disabled={submitting}
          style={{ ...sty.btn, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'ログイン中...' : 'ログイン'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button onClick={onResetClick} style={{ color: TIFFANY, fontSize: 13, cursor: 'pointer', background: 'none', border: 'none', padding: 0, textDecoration: 'underline' }}>
            パスワードを忘れた方はこちら
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
          初めてご利用ですか？{' '}
         <button onClick={onSignupClick} style={{ color: TIFFANY, fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, fontSize: 'inherit' }}>
          会員登録はこちら
        </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SignupView (Supabase認証版)
// ============================================================
function SignupView({ onLoginClick }) {
  const [form, setForm] = useState({
    name: '', musashiId: '', email: '', password: '', passwordConfirm: '',
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSignup() {
    setErrorMsg('');
    if (!form.name.trim() || !form.musashiId.trim() || !form.email.trim() || !form.password) {
      haptic('error');
      setErrorMsg('お名前・ムサシID・メールアドレス・パスワードは必須です');
      return;
    }
    if (form.password.length < 6) {
      haptic('error');
      setErrorMsg('パスワードは6文字以上で設定してください');
      return;
    }
    if (form.password !== form.passwordConfirm) {
      haptic('error');
      setErrorMsg('パスワードが一致しません');
      return;
    }

    setSubmitting(true);
    try {
      const { data: existing, error: chkErr } = await supabase
        .from('profiles')
        .select('id')
        .eq('musashi_id', form.musashiId.trim())
        .maybeSingle();
      if (chkErr && chkErr.code !== 'PGRST116') {
        console.warn('check err:', chkErr);
      }
      if (existing) {
        haptic('error');
        setErrorMsg('このムサシIDは既に登録されています');
        setSubmitting(false);
        return;
      }

      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: {
          data: {
            name: form.name.trim(),
            musashi_id: form.musashiId.trim(),
            phone: form.phone.trim() || null,
          },
          emailRedirectTo: window.location.origin,
        },
      });
      if (authErr) {
        haptic('error');
        if (authErr.message.includes('already registered')) {
          setErrorMsg('このメールアドレスは既に登録されています');
        } else {
          setErrorMsg(authErr.message);
        }
        return;
      }

      if (authData.user) {
        const { error: profErr } = await supabase.from('profiles').insert({
          id: authData.user.id,
          name: form.name.trim(),
          musashi_id: form.musashiId.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
        });
        if (profErr) {
          console.warn('Profile creation:', profErr);
        }
      }

      haptic('success');
      setSuccess(true);
    } catch (e) {
      console.error(e);
      haptic('error');
      setErrorMsg('登録に失敗しました: ' + (e.message || ''));
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div style={{ padding: '32px 16px', maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', background: TIFFANY,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <IconCheck size={40} color="white" strokeWidth={3} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 }}>
          確認メールを送信しました
        </h2>
        <div style={sty.cardLg}>
          <p style={{ color: '#475569', lineHeight: 1.6 }}>
            <strong>{form.email}</strong><br/>
            に確認メールを送信しました。
          </p>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 16 }}>
            メール内のリンクをクリックして認証を完了してください。
            認証後、ログインできるようになります。
          </p>
          <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 16 }}>
            メールが届かない場合は、迷惑メールフォルダもご確認ください。
          </p>
          <button onClick={onLoginClick}
            style={{ ...sty.btnSec, marginTop: 16, padding: '10px 24px', borderRadius: 999 }}>
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 420, margin: '0 auto' }}>
      <button onClick={onLoginClick} style={sty.btnGhost}>
        <IconChevronLeft size={16} /> ログイン画面に戻る
      </button>
      <div style={{ textAlign: 'center', margin: '16px 0 24px' }}>
        <div style={{ fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold' }}>
          MEMBER SIGNUP
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', margin: '4px 0 0' }}>
          会員登録
        </h2>
      </div>
      <div style={sty.cardLg}>
        {errorMsg && (
          <div style={{
            padding: 12, marginBottom: 12, background: '#fef2f2',
            border: '1px solid #fca5a5', borderRadius: 8,
            color: '#b91c1c', fontSize: 13,
          }}>
            {errorMsg}
          </div>
        )}
        <Field label="お名前" required>
          <input type="text" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="山田 太郎" style={sty.input} />
        </Field>
        <Field label="ムサシポーカールームID" required>
          <input type="text" value={form.musashiId}
            onChange={(e) => setForm({ ...form, musashiId: e.target.value })}
            placeholder="例: M12345" style={sty.input} autoCapitalize="off" />
        </Field>
        <Field label="メールアドレス" required>
          <input type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="example@example.com" style={sty.input}
            autoCapitalize="off" autoComplete="email" />
        </Field>
        <Field label="パスワード (6文字以上)" required>
          <input type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="••••••••" style={sty.input}
            autoComplete="new-password" />
        </Field>
        <Field label="パスワード(確認)" required>
          <input type="password" value={form.passwordConfirm}
            onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })}
            placeholder="••••••••" style={sty.input}
            autoComplete="new-password" />
        </Field>
        <Field label="電話番号 (任意)">
          <input type="tel" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="090-0000-0000" style={sty.input} />
        </Field>
        <button onClick={handleSignup} disabled={submitting}
          style={{ ...sty.btn, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '登録中...' : '登録する'}
        </button>
        <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
          登録後、確認メールを送信します。
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PasswordResetView
// ============================================================
function PasswordResetView({ onLoginClick }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function handleReset() {
    setErrorMsg('');
    if (!email.trim()) {
      haptic('error');
      setErrorMsg('メールアドレスを入力してください');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + '/?reset=true',
      });
      if (error) {
        haptic('error');
        setErrorMsg(error.message);
        return;
      }
      haptic('success');
      setSuccess(true);
    } catch (e) {
      console.error(e);
      haptic('error');
      setErrorMsg('送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div style={{ padding: '32px 16px', maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', background: TIFFANY,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <IconCheck size={40} color="white" strokeWidth={3} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 }}>
          リセットメールを送信しました
        </h2>
        <div style={sty.cardLg}>
          <p style={{ color: '#475569', lineHeight: 1.6 }}>
            <strong>{email}</strong><br/>
            にパスワードリセット用のメールを送信しました。
          </p>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 16 }}>
            メール内のリンクをクリックして、新しいパスワードを設定してください。
          </p>
          <button onClick={onLoginClick}
            style={{ ...sty.btnSec, marginTop: 16, padding: '10px 24px', borderRadius: 999 }}>
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 420, margin: '0 auto' }}>
      <button onClick={onLoginClick} style={sty.btnGhost}>
        <IconChevronLeft size={16} /> ログイン画面に戻る
      </button>
      <div style={{ textAlign: 'center', margin: '16px 0 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b' }}>
          パスワードリセット
        </h2>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 8 }}>
          ご登録のメールアドレスに<br/>リセット用のリンクを送信します
        </p>
      </div>
      <div style={sty.cardLg}>
        {errorMsg && (
          <div style={{
            padding: 12, marginBottom: 12, background: '#fef2f2',
            border: '1px solid #fca5a5', borderRadius: 8,
            color: '#b91c1c', fontSize: 13,
          }}>
            {errorMsg}
          </div>
        )}
        <Field label="メールアドレス" required>
          <input type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleReset(); }}
            placeholder="example@example.com" style={sty.input}
            autoCapitalize="off" />
        </Field>
        <button onClick={handleReset} disabled={submitting}
          style={{ ...sty.btn, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '送信中...' : 'リセットメールを送信'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// PasswordUpdateView (リセットリンクから来た場合)
// ============================================================
function PasswordUpdateView({ onComplete }) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function handleUpdate() {
    setErrorMsg('');
    if (password.length < 6) {
      haptic('error'); setErrorMsg('パスワードは6文字以上'); return;
    }
    if (password !== passwordConfirm) {
      haptic('error'); setErrorMsg('パスワードが一致しません'); return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) { haptic('error'); setErrorMsg(error.message); return; }
      haptic('success');
      alert('パスワードを更新しました');
      onComplete();
    } catch (e) {
      haptic('error'); setErrorMsg('更新に失敗しました');
    } finally { setSubmitting(false); }
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 420, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', margin: '16px 0 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b' }}>
          新しいパスワード設定
        </h2>
      </div>
      <div style={sty.cardLg}>
        {errorMsg && (
          <div style={{
            padding: 12, marginBottom: 12, background: '#fef2f2',
            border: '1px solid #fca5a5', borderRadius: 8,
            color: '#b91c1c', fontSize: 13,
          }}>{errorMsg}</div>
        )}
        <Field label="新しいパスワード (6文字以上)" required>
          <input type="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={sty.input} autoComplete="new-password" />
        </Field>
        <Field label="新しいパスワード(確認)" required>
          <input type="password" value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            style={sty.input} autoComplete="new-password" />
        </Field>
        <button onClick={handleUpdate} disabled={submitting}
          style={{ ...sty.btn, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '更新中...' : 'パスワードを更新'}
        </button>
      </div>
    </div>
  );
}


// ============================================================
// MyReservationsView
// ============================================================
function MyReservationsView({ user, reservations, slots, onCancel }) {
  const [tab, setTab] = useState('upcoming');

  const slotMap = {};
  for (const s of (slots || [])) { if (s && s.id) slotMap[s.id] = s; }

  const myResRaw = (reservations || []).filter((r) => r && r.user_id === user.id);
  const seenSlots = new Set();
  const myRes = [];
  const sortedByOld = [...myResRaw].sort((a, b) => {
    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
  });
  for (const r of sortedByOld) {
    if (!seenSlots.has(r.slot_id)) {
      seenSlots.add(r.slot_id);
      myRes.push(r);
    }
  }
  const todayStr = toLocalDateStr();

  const upcoming = myRes.filter((r) => {
    const s = slotMap[r.slot_id];
    return s && s.date >= todayStr;
  }).sort((a, b) => {
    const sa = slotMap[a.slot_id] || {}; const sb = slotMap[b.slot_id] || {};
    return ((sa.date || '') + (sa.time || '')).localeCompare((sb.date || '') + (sb.time || ''));
  });

  const past = myRes.filter((r) => {
    const s = slotMap[r.slot_id];
    return s && s.date < todayStr;
  }).sort((a, b) => {
    const sa = slotMap[a.slot_id] || {}; const sb = slotMap[b.slot_id] || {};
    return ((sb.date || '') + (sb.time || '')).localeCompare((sa.date || '') + (sa.time || ''));
  });

  const list = tab === 'upcoming' ? upcoming : past;

  async function handleCancel(r) {
    if (!confirm('この予約をキャンセルしますか?')) return;
    try {
      await onCancel(r.id);
      haptic('warning');
    } catch (e) {
      haptic('error');
      alert('キャンセルに失敗しました');
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', margin: '8px 0 16px' }}>
        マイ予約
      </h2>
      <div style={{
        display: 'flex', background: 'white', borderRadius: 10, padding: 4,
        marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <button onClick={() => setTab('upcoming')} style={{
          flex: 1, padding: '8px', border: 'none', borderRadius: 8,
          background: tab === 'upcoming' ? TIFFANY : 'transparent',
          color: tab === 'upcoming' ? 'white' : '#64748b',
          fontWeight: 'bold', cursor: 'pointer',
        }}>今後 ({upcoming.length})</button>
        <button onClick={() => setTab('past')} style={{
          flex: 1, padding: '8px', border: 'none', borderRadius: 8,
          background: tab === 'past' ? TIFFANY : 'transparent',
          color: tab === 'past' ? 'white' : '#64748b',
          fontWeight: 'bold', cursor: 'pointer',
        }}>過去 ({past.length})</button>
      </div>
      {list.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <IconCalendar size={32} color="#cbd5e1" />
          <div style={{ marginTop: 8 }}>
            {tab === 'upcoming' ? '今後の予約はありません' : '過去の予約はありません'}
          </div>
        </div>
      ) : (
        list.map((r) => {
          const s = slotMap[r.slot_id] || {};
          return (
            <div key={r.id} style={sty.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{s.title || ''}</div>
                  <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b' }}>
                    {formatDateFull(s.date)} {s.time}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    予約日時: {r.created_at ? new Date(r.created_at).toLocaleString('ja-JP') : '-'}
                  </div>
                </div>
                {tab === 'upcoming' && (
                  <button onClick={() => handleCancel(r)} style={{
                    padding: '6px 12px', background: 'transparent',
                    color: '#dc2626', border: '1px solid #fca5a5',
                    borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold',
                  }}>キャンセル</button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ============================================================
// ProfileView
// ============================================================
function ProfileView({ user, profile, onUpdate, onLogout }) {
  const [form, setForm] = useState({
    name: profile.name || '',
    phone: profile.phone || '',
    email: profile.email || '',
    newPw: '', newPwConfirm: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  async function saveProfile() {
    setMsg('');
    setSaving(true);
    try {
      await onUpdate({
        name: form.name.trim(),
        phone: form.phone.trim() || null,
      });
      haptic('success');
      setMsg('プロフィールを更新しました');
    } catch (e) {
      haptic('error');
      setMsg('更新に失敗しました: ' + (e.message || ''));
    } finally { setSaving(false); }
  }

  async function changePassword() {
    setMsg('');
    if (!form.newPw || !form.newPwConfirm) {
      haptic('error'); setMsg('新パスワードを入力してください'); return;
    }
    if (form.newPw.length < 6) {
      haptic('error'); setMsg('パスワードは6文字以上'); return;
    }
    if (form.newPw !== form.newPwConfirm) {
      haptic('error'); setMsg('新パスワードが一致しません'); return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: form.newPw });
      if (error) {
        haptic('error'); setMsg('変更失敗: ' + error.message); return;
      }
      setForm({ ...form, newPw: '', newPwConfirm: '' });
      haptic('success'); setMsg('パスワードを変更しました');
    } catch (e) {
      haptic('error'); setMsg('変更に失敗しました');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', margin: '8px 0 16px' }}>
        プロフィール
      </h2>
      {msg && (
        <div style={{
          padding: 12, marginBottom: 12,
          background: msg.includes('失敗') ? '#fef2f2' : '#f0fdfa',
          border: '1px solid ' + (msg.includes('失敗') ? '#fca5a5' : '#5eead4'),
          borderRadius: 8,
          color: msg.includes('失敗') ? '#b91c1c' : '#0f766e',
          fontSize: 13,
        }}>{msg}</div>
      )}
      <div style={sty.cardLg}>
        <Field label="ムサシID">
          <input type="text" value={profile.musashi_id || ''} disabled
            style={{ ...sty.input, background: '#f1f5f9', color: '#94a3b8' }} />
        </Field>
        <Field label="メールアドレス">
          <input type="email" value={form.email} disabled
            style={{ ...sty.input, background: '#f1f5f9', color: '#94a3b8' }} />
        </Field>
        <Field label="お名前" required>
          <input type="text" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} style={sty.input} />
        </Field>
        <Field label="電話番号">
          <input type="tel" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="090-0000-0000" style={sty.input} />
        </Field>
        <button onClick={saveProfile} disabled={saving} style={sty.btn}>
          {saving ? '保存中...' : 'プロフィールを保存'}
        </button>
      </div>

      <div style={{ ...sty.cardLg, marginTop: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b', marginTop: 0 }}>
          パスワード変更
        </h3>
        <Field label="新しいパスワード (6文字以上)">
          <input type="password" value={form.newPw}
            onChange={(e) => setForm({ ...form, newPw: e.target.value })} style={sty.input} />
        </Field>
        <Field label="新しいパスワード(確認)">
          <input type="password" value={form.newPwConfirm}
            onChange={(e) => setForm({ ...form, newPwConfirm: e.target.value })} style={sty.input} />
        </Field>
        <button onClick={changePassword} disabled={saving}
          style={{ ...sty.btn, background: '#475569' }}>
          {saving ? '変更中...' : 'パスワードを変更'}
        </button>
      </div>

      <button onClick={onLogout}
        style={{
          width: '100%', marginTop: 16, padding: '12px',
          background: 'transparent', color: '#dc2626',
          border: '1px solid #fca5a5', borderRadius: 10,
          fontSize: 14, fontWeight: 'bold', cursor: 'pointer',
        }}>
        ログアウト
      </button>
    </div>
  );
}


// ============================================================
// SlotDetailView (枠の詳細画面 - NEW!)
// ============================================================
function SlotDetailView({ slot, reserved, alreadyReserved, onBack, onReserve }) {
  const [imgIdx, setImgIdx] = useState(0);
  const touchStartRef = useRef(null);

  if (!slot) return null;

  const images = Array.isArray(slot.images) ? slot.images.filter(Boolean) : [];
  const cap = slot.capacity || 0;
  const dummy = slot.dummy_reserved || 0;
  const totalReserved = reserved + dummy;
  const remaining = Math.max(0, cap - totalReserved);
  const isFull = remaining <= 0;
  const canReserve = !isFull && !alreadyReserved;

  function handleTouchStart(e) {
    if (!e.touches || !e.touches[0]) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
  }
  function handleTouchEnd(e) {
    const start = touchStartRef.current;
    if (!start || !e.changedTouches || !e.changedTouches[0]) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    touchStartRef.current = null;
    if (Math.abs(dx) < 50) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dt > 1000) return;
    if (images.length < 2) return;
    if (dx < 0 && imgIdx < images.length - 1) {
      haptic('light'); setImgIdx(imgIdx + 1);
    } else if (dx > 0 && imgIdx > 0) {
      haptic('light'); setImgIdx(imgIdx - 1);
    }
  }

  return (
    <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>
      <button onClick={() => { haptic('light'); onBack(); }} style={sty.btnGhost}>
        <IconChevronLeft size={16} /> 戻る
      </button>

      {/* 画像スワイプエリア */}
      {images.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
              position: 'relative',
              borderRadius: 16, overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              background: '#f1f5f9',
              aspectRatio: '1 / 1',
            }}
          >
            <img
              src={images[imgIdx]}
              alt=""
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', display: 'block',
                transition: 'opacity 0.2s',
              }}
            />
            {images.length > 1 && (
              <>
                {/* 左右ナビゲーション (PCでもクリック可) */}
                {imgIdx > 0 && (
                  <button onClick={() => { haptic('light'); setImgIdx(imgIdx - 1); }} style={{
                    position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.9)', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}>
                    <IconChevronLeft size={18} color="#1e293b" />
                  </button>
                )}
                {imgIdx < images.length - 1 && (
                  <button onClick={() => { haptic('light'); setImgIdx(imgIdx + 1); }} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.9)', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}>
                    <IconChevronRight size={18} color="#1e293b" />
                  </button>
                )}
                {/* インジケータドット */}
                <div style={{
                  position: 'absolute', bottom: 12, left: 0, right: 0,
                  display: 'flex', justifyContent: 'center', gap: 6,
                }}>
                  {images.map((_, i) => (
                    <div key={i} style={{
                      width: i === imgIdx ? 24 : 8, height: 8, borderRadius: 4,
                      background: i === imgIdx ? 'white' : 'rgba(255,255,255,0.5)',
                      transition: 'all 0.3s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* イベント情報 */}
      <div style={{ ...sty.cardLg, marginTop: 16 }}>
        {slot.recommended && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 'bold', color: '#92400e',
            background: '#fef3c7', padding: '3px 8px', borderRadius: 4,
            marginBottom: 8,
          }}>
            <IconStar size={11} color="#92400e" fill="#92400e" />
            オススメ
          </div>
        )}
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
          {formatDateFull(slot.date)}
        </div>
        <div style={{ fontSize: 26, fontWeight: 'bold', color: '#1e293b', lineHeight: 1.2 }}>
          {slot.time}
        </div>
        <div style={{ fontSize: 16, color: '#475569', marginTop: 4, fontWeight: 500 }}>
          {slot.title || ''}
        </div>

        <div style={{
          marginTop: 12, display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 12px', background: '#f8fafc', borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, color: '#64748b' }}>定員</div>
          <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>
            {totalReserved}/{cap}名
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: '#64748b' }}>残り</div>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: isFull ? '#94a3b8' : TIFFANY }}>
            {remaining}席
          </div>
        </div>
      </div>

      {/* 詳細説明 */}
      {slot.description && (
        <div style={{ ...sty.cardLg }}>
          <div style={{ fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold', marginBottom: 8 }}>
            EVENT DETAILS
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b', marginTop: 0, marginBottom: 12 }}>
            イベント詳細
          </h3>
          <div style={{
            fontSize: 14, color: '#334155', lineHeight: 1.8,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {slot.description}
          </div>
        </div>
      )}

      {/* 予約ボタン */}
      <div style={{
        position: 'sticky', bottom: 16, marginTop: 16,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
        padding: 12, borderRadius: 16,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
      }}>
        <button
          disabled={!canReserve}
          onClick={() => { if (canReserve) { haptic('medium'); onReserve(); } }}
          style={{
            ...sty.btn,
            background: alreadyReserved ? '#fef3c7' : (isFull ? '#f1f5f9' : TIFFANY),
            color: alreadyReserved ? '#92400e' : (isFull ? '#94a3b8' : 'white'),
            cursor: canReserve ? 'pointer' : 'not-allowed',
            opacity: canReserve ? 1 : 0.7,
            fontSize: 16, padding: '14px',
          }}
        >
          {alreadyReserved ? '✓ 予約済み' : (isFull ? '満席' : 'この枠を予約する →')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// CustomerView (お客様画面 - ログイン後)
// ============================================================
function CustomerView({ user, profile, slots, reservations, hours, ads, onReserve, lastTab, onTabChange, onGoToMyReservations, resetKey }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState('select');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(lastTab || 'recommend');
  const [selectedAd, setSelectedAd] = useState(null); // 広告タップ時のモーダル用
  const touchStartRef = useRef(null);

  // 外部からのリセット指示（ロゴクリック等）に反応
  useEffect(() => {
    if (resetKey === undefined || resetKey === null) return;
    setStep('select');
    setSelectedSlot(null);
    setSelectedAd(null);
  }, [resetKey]);

  function changeTab(newTab) {
    setActiveTab(newTab);
    if (onTabChange) onTabChange(newTab);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toLocalDateStr(today);

  const dateKeys = ['recommend'];
  for (let i = 0; i < 21; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    dateKeys.push(toLocalDateStr(d));
  }

  const safeSlots = Array.isArray(slots) ? slots : [];
  let displaySlots = [];
  if (activeTab === 'recommend') {
    displaySlots = safeSlots
      .filter((s) => s && s.recommended && s.date >= todayStr)
      .sort((a, b) => ((a.date || '') + (a.time || '')).localeCompare((b.date || '') + (b.time || '')));
  } else {
    displaySlots = safeSlots
      .filter((s) => s && s.date === activeTab)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }

  const myReservedSlotIds = new Set(
    (reservations || [])
      .filter((r) => r && r.user_id === user.id)
      .map((r) => r.slot_id)
  );

  const reservationsBySlot = {};
  for (const r of (reservations || [])) {
    if (r && r.slot_id) {
      reservationsBySlot[r.slot_id] = (reservationsBySlot[r.slot_id] || 0) + 1;
    }
  }

  function handleTouchStart(e) {
    if (!e.touches || !e.touches[0]) return;
    // 日付タブ・広告カルーセル内でのタッチは無視（それぞれの内部スワイプに任せる）
    const target = e.target;
    if (target && target.closest) {
      if (target.closest('[data-date-tabs="true"]')) {
        touchStartRef.current = null;
        return;
      }
      if (target.closest('[data-ad-carousel="true"]')) {
        touchStartRef.current = null;
        return;
      }
    }
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
  }
  function handleTouchEnd(e) {
    const start = touchStartRef.current;
    if (!start || !e.changedTouches || !e.changedTouches[0]) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    touchStartRef.current = null;
    // 横移動が60px未満は無視（誤動作防止）
    if (Math.abs(dx) < 60) return;
    // 縦移動の方が大きい場合は縦スクロールとみなしてキャンセル
    if (Math.abs(dy) > Math.abs(dx)) return;
    // 1秒以上のゆっくり動作はスワイプじゃないので無視
    if (dt > 1000) return;

    // オススメタブの場合は「最初の日付タブ」を起点にする
    let idx;
    if (activeTab === 'recommend') {
      // オススメ → 左へスワイプ(dx<0): 最初の日付タブへ
      // オススメ → 右へスワイプ(dx>0): 最後の日付タブへ
      if (dx < 0) {
        // dateKeys[1] が最初の日付（dateKeys[0]は'recommend'）
        if (dateKeys.length > 1) {
          haptic('light');
          changeTab(dateKeys[1]);
        }
      } else {
        // 右スワイプは循環せず無視（オススメが最初なので前はない）
      }
      return;
    }

    idx = dateKeys.indexOf(activeTab);
    if (idx < 0) return;
    if (dx < 0 && idx < dateKeys.length - 1) {
      haptic('light');
      changeTab(dateKeys[idx + 1]);
    } else if (dx > 0 && idx > 0) {
      // 日付タブから右スワイプ → 前の日付 (もしくはオススメに戻る)
      haptic('light');
      changeTab(dateKeys[idx - 1]);
    }
  }

  // ----- 完了画面（強化版！）-----
  if (step === 'done' && selectedSlot) {
    return (
      <div style={{
        padding: '32px 16px', maxWidth: 480, margin: '0 auto', textAlign: 'center',
        position: 'relative',
      }}>
        {/* キラキラ装飾 */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
        }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: `${(i * 13 + 7) % 100}%`,
              top: `${(i * 17 + 13) % 80}%`,
              fontSize: 16 + (i % 3) * 6,
              opacity: 0,
              animation: `confettiPop 1.2s ease-out ${i * 0.06}s both`,
            }}>
              {['🎉', '✨', '🎊', '⭐'][i % 4]}
            </div>
          ))}
        </div>

        {/* チェックマーク（リング演出付き） */}
        <div style={{
          position: 'relative', display: 'inline-block', marginBottom: 24,
        }}>
          {/* リング3層 */}
          <div style={{
            position: 'absolute', inset: -20,
            border: `2px solid ${TIFFANY}`, borderRadius: '50%',
            opacity: 0,
            animation: 'doneRing 1.6s ease-out 0.2s both',
          }} />
          <div style={{
            position: 'absolute', inset: -20,
            border: `2px solid ${TIFFANY}`, borderRadius: '50%',
            opacity: 0,
            animation: 'doneRing 1.6s ease-out 0.5s both',
          }} />
          <div style={{
            width: 96, height: 96, borderRadius: '50%', background: TIFFANY,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 10px 40px rgba(10,186,181,0.4)',
            animation: 'donePop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}>
            <IconCheck size={50} color="white" strokeWidth={3} />
          </div>
        </div>

        <div style={{
          fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold', marginBottom: 8,
          animation: 'fadeUp 0.6s ease-out 0.4s both',
        }}>
          RESERVATION CONFIRMED
        </div>
        <h2 style={{
          fontSize: 24, fontWeight: 'bold', color: '#1e293b', marginBottom: 8,
          animation: 'fadeUp 0.6s ease-out 0.5s both',
        }}>
          ご予約を承りました
        </h2>
        <p style={{
          fontSize: 13, color: '#64748b', marginBottom: 24,
          animation: 'fadeUp 0.6s ease-out 0.6s both',
        }}>
          ご来店を心よりお待ちしております
        </p>

        <div style={{
          ...sty.cardLg, textAlign: 'left',
          animation: 'fadeUp 0.6s ease-out 0.7s both',
        }}>
          <Row label="日程" value={`${formatDateFull(selectedSlot.date)} ${selectedSlot.time}`} />
          {selectedSlot.title && <Row label="種別" value={selectedSlot.title} />}
          <Row label="お名前" value={profile.name} />
          <Row label="ムサシID" value={profile.musashi_id} />
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap',
          animation: 'fadeUp 0.6s ease-out 0.8s both',
        }}>
          <button
            onClick={() => { haptic('light'); if (onGoToMyReservations) onGoToMyReservations(); }}
            style={{
              padding: '12px 24px', borderRadius: 999,
              background: TIFFANY, color: 'white', border: 'none',
              fontWeight: 'bold', fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(10,186,181,0.3)',
            }}>
            マイ予約を見る
          </button>
          <button
            onClick={() => { haptic('light'); setStep('select'); setSelectedSlot(null); }}
            style={{ ...sty.btnSec, padding: '12px 24px', borderRadius: 999 }}>
            続けて予約する
          </button>
        </div>

        <style>{`
          @keyframes donePop {
            0% { transform: scale(0); opacity: 0; }
            60% { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes doneRing {
            0% { opacity: 0.8; transform: scale(0.8); }
            100% { opacity: 0; transform: scale(1.8); }
          }
          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(12px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes confettiPop {
            0% { opacity: 0; transform: translateY(20px) scale(0); }
            50% { opacity: 1; transform: translateY(0) scale(1); }
            100% { opacity: 0; transform: translateY(-30px) scale(0.6); }
          }
        `}</style>
      </div>
    );
  }

  // ----- 詳細画面 -----
  if (step === 'detail' && selectedSlot) {
    return (
      <SlotDetailView
        slot={selectedSlot}
        reserved={reservationsBySlot[selectedSlot.id] || 0}
        alreadyReserved={myReservedSlotIds.has(selectedSlot.id)}
        onBack={() => { setStep('select'); }}
        onReserve={() => { setStep('confirm'); }}
      />
    );
  }

  // ----- 確認画面 -----
  if (step === 'confirm' && selectedSlot) {
    const alreadyReserved = myReservedSlotIds.has(selectedSlot.id);
    return (
      <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>
        <button onClick={() => { haptic('light'); setStep('select'); setSelectedSlot(null); }} style={sty.btnGhost}>
          <IconChevronLeft size={16} /> 戻る
        </button>
        <div style={{ fontSize: 10, letterSpacing: '0.3em', color: TIFFANY, fontWeight: 'bold', marginTop: 16, marginBottom: 4 }}>
          CONFIRMATION
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1e293b', marginBottom: 16 }}>
          予約内容の確認
        </h2>

        <div style={sty.cardLg}>
          <Row label="日程" value={`${formatDateFull(selectedSlot.date)} ${selectedSlot.time}`} />
          {selectedSlot.title && <Row label="種別" value={selectedSlot.title} />}
          <Row label="お名前" value={profile.name} />
          <Row label="ムサシID" value={profile.musashi_id} />
          {profile.phone && <Row label="電話番号" value={profile.phone} />}

          {alreadyReserved && (
            <div style={{
              marginTop: 12, padding: 12, background: '#fef3c7',
              border: '1px solid #fbbf24', borderRadius: 8,
              color: '#92400e', fontSize: 13,
            }}>
              ⚠️ この予約枠は既に予約済みです。重複予約はできません。
            </div>
          )}

          <button
            disabled={submitting || alreadyReserved}
            onClick={async () => {
              if (alreadyReserved) return;
              setSubmitting(true);
              try {
                await onReserve({
                  slot_id: selectedSlot.id,
                  user_id: user.id,
                  name: profile.name,
                  musashi_id: profile.musashi_id,
                  phone: profile.phone || null,
                });
                haptic('success'); setStep('done');
              } catch (e) {
                haptic('error'); alert('予約に失敗しました: ' + (e.message || ''));
              } finally { setSubmitting(false); }
            }}
            style={{
              ...sty.btn, marginTop: 16,
              opacity: (submitting || alreadyReserved) ? 0.5 : 1,
              cursor: (submitting || alreadyReserved) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            {submitting && (
              <span style={{
                display: 'inline-block', width: 16, height: 16,
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: 'white', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
            )}
            {alreadyReserved ? '既に予約済み' : (submitting ? '送信中...' : 'この内容で予約する')}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ----- 枠選択画面 -----
  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ minHeight: 'calc(100vh - 180px)', touchAction: 'pan-y' }}
    >
      <DateTabs activeKey={activeTab} onChange={changeTab} />
      {activeTab !== 'recommend' && (
        <HoursBanner date={activeTab} hours={(hours || {})[activeTab]} />
      )}

      {ads && ads.length > 0 && activeTab === 'recommend' && (
        <AdCarousel ads={ads} onAdClick={(ad) => setSelectedAd(ad)} />
      )}

      {/* 広告タップ時の関連枠モーダル */}
      {selectedAd && (
        <AdSlotsModal
          ad={selectedAd}
          slots={slots}
          reservations={reservations}
          onClose={() => setSelectedAd(null)}
          onSelectSlot={(slot) => {
            setSelectedAd(null);
            setSelectedSlot(slot);
            setStep('detail');
          }}
        />
      )}

      <div style={{
        padding: '12px 16px', maxWidth: 480, margin: '0 auto',
        minHeight: 'calc(100vh - 280px)',
      }}>
        {activeTab === 'recommend' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, padding: '0 4px' }}>
              <IconStar size={16} color={TIFFANY} fill={TIFFANY} />
              <h2 style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b', margin: 0 }}>オススメ予約</h2>
            </div>
            {(() => {
              const groups = groupByDate(displaySlots);
              const dates = Object.keys(groups).sort();
              if (dates.length === 0) {
                return (
                  <div style={{
                    padding: '40px 20px', textAlign: 'center', color: '#94a3b8',
                    minHeight: 400, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <IconStar size={32} color="#cbd5e1" />
                    <div style={{ marginTop: 8 }}>オススメ予約はありません</div>
                    <div style={{ fontSize: 11, marginTop: 16, color: '#cbd5e1' }}>
                      ← スワイプで日付を切替 →
                    </div>
                  </div>
                );
              }
              return dates.map((date) => (
                <div key={date}>
                  <DateHeader dateStr={date} />
                  {groups[date].map((slot) => (
                    <SlotCard
                      key={slot.id} slot={slot}
                      reserved={reservationsBySlot[slot.id] || 0}
                      alreadyReserved={myReservedSlotIds.has(slot.id)}
                      onSelect={() => { setSelectedSlot(slot); setStep('confirm'); }}
                      onShowDetail={() => { setSelectedSlot(slot); setStep('detail'); }}
                    />
                  ))}
                </div>
              ));
            })()}
          </>
        ) : (
          <>
            {displaySlots.length === 0 ? (
              <div style={{
                padding: '40px 20px', textAlign: 'center', color: '#94a3b8',
                minHeight: 400, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <IconCalendar size={32} color="#cbd5e1" />
                <div style={{ marginTop: 8 }}>この日の予約枠はありません</div>
                <div style={{ fontSize: 11, marginTop: 16, color: '#cbd5e1' }}>
                  ← スワイプで日付を切替 →
                </div>
              </div>
            ) : (
              displaySlots.map((slot) => (
                <SlotCard
                  key={slot.id} slot={slot}
                  reserved={reservationsBySlot[slot.id] || 0}
                  alreadyReserved={myReservedSlotIds.has(slot.id)}
                  onSelect={() => { setSelectedSlot(slot); setStep('confirm'); }}
                  onShowDetail={() => { setSelectedSlot(slot); setStep('detail'); }}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================
// AdSlotPicker (管理画面: 広告に関連付ける予約枠を選択するモーダル)
// ============================================================
function AdSlotPicker({ ad, slots, onClose, onSave }) {
  const initialIds = (typeof ad === 'string') ? [] : (ad.slot_ids || []);
  const [selected, setSelected] = useState(new Set(initialIds));
  const [filter, setFilter] = useState('');
  const dataUrl = typeof ad === 'string' ? ad : ad.data_url;

  function toggle(slotId) {
    const next = new Set(selected);
    if (next.has(slotId)) next.delete(slotId);
    else next.add(slotId);
    setSelected(next);
    haptic('light');
  }

  // 今日以降の枠を日付順に
  const todayStr = toLocalDateStr();
  const sortedSlots = [...(slots || [])]
    .filter((s) => s && s.date && s.date >= todayStr)
    .sort((a, b) => ((a.date || '') + (a.time || '')).localeCompare((b.date || '') + (b.time || '')));

  const filteredSlots = sortedSlots.filter((s) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (s.title || '').toLowerCase().includes(q)
      || (s.date || '').includes(filter)
      || (s.time || '').includes(filter);
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 600, maxHeight: '90vh',
          background: 'white', borderRadius: '20px 20px 0 0',
          padding: 16, display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{
          width: 40, height: 4, background: '#cbd5e1', borderRadius: 2,
          margin: '0 auto 12px',
        }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <img src={dataUrl} alt="" style={{
            width: 50, height: 50, objectFit: 'cover', borderRadius: 6,
            background: '#f1f5f9',
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 'bold', color: '#1e293b' }}>
              関連イベントを選択
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              選択中: {selected.size}件
            </div>
          </div>
          <button onClick={onClose} style={{
            background: '#f1f5f9', border: 'none', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconX size={16} color="#64748b" />
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 10 }}>
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
            <IconSearch size={14} color="#94a3b8" />
          </div>
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="イベント名・日付・時刻で検索"
            style={{ ...sty.input, paddingLeft: 32, fontSize: 13 }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 10 }}>
          {filteredSlots.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {sortedSlots.length === 0
                ? '予約枠がまだありません'
                : '該当するイベントがありません'}
            </div>
          ) : (
            filteredSlots.map((slot) => {
              const isSelected = selected.has(slot.id);
              return (
                <div
                  key={slot.id}
                  onClick={() => toggle(slot.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: 10, marginBottom: 6,
                    background: isSelected ? 'rgba(10,186,181,0.08)' : '#f8fafc',
                    border: `2px solid ${isSelected ? TIFFANY : 'transparent'}`,
                    borderRadius: 8, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 6,
                    background: isSelected ? TIFFANY : 'white',
                    border: `2px solid ${isSelected ? TIFFANY : '#cbd5e1'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {isSelected && <IconCheck size={14} color="white" strokeWidth={3} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>
                      {slot.recommended && <span style={{ color: '#f59e0b' }}>★ </span>}
                      {formatDateFull(slot.date)} {slot.time}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {slot.title} / 定員{slot.capacity}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
          <button onClick={onClose} style={{ ...sty.btnSec, flex: 1 }}>
            キャンセル
          </button>
          <button onClick={() => onSave(Array.from(selected))} style={{ ...sty.btn, flex: 1 }}>
            保存 ({selected.size}件)
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// AdminSlotsView (枠管理)
// ============================================================
function AdminSlotsView({ slots, onSave, ads, onAdsChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: '', time: '19:00', capacity: 30, title: 'レギュラーマッチ',
    recommended: false, dummy_reserved: 0,
    description: '', images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [slotImageUploading, setSlotImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  const slotImageInputRef = useRef(null);

  function startNew() {
    const d = new Date(); d.setDate(d.getDate() + 1);
    setForm({
      date: toLocalDateStr(d), time: '19:00', capacity: 30,
      title: 'レギュラーマッチ', recommended: false, dummy_reserved: 0,
      description: '', images: [],
    });
    setEditing('new');
  }

  function startEdit(slot) {
    setForm({
      date: slot.date || '', time: slot.time || '19:00',
      capacity: slot.capacity || 30, title: slot.title || '',
      recommended: !!slot.recommended, dummy_reserved: slot.dummy_reserved || 0,
      description: slot.description || '',
      images: Array.isArray(slot.images) ? [...slot.images] : [],
    });
    setEditing(slot.id);
  }

  async function save() {
    if (!form.date || !form.time) { alert('日付と時刻を入力'); return; }
    let next;
    if (editing === 'new') {
      const id = `slot-${form.date}-${form.time.replace(':', '')}-${Date.now()}`;
      next = [...(slots || []), { id, ...form, capacity: Number(form.capacity), dummy_reserved: Number(form.dummy_reserved) }];
    } else {
      next = (slots || []).map((s) => s.id === editing ? { ...s, ...form, capacity: Number(form.capacity), dummy_reserved: Number(form.dummy_reserved) } : s);
    }
    await onSave(next);
    haptic('success');
    setEditing(null);
  }

  async function handleSlotImageUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if ((form.images || []).length >= 3) {
      alert('画像は最大3枚までです');
      if (slotImageInputRef.current) slotImageInputRef.current.value = '';
      return;
    }
    setSlotImageUploading(true);
    try {
      const dataUrl = await compressImage(file, 1024, 0.85);
      setForm({ ...form, images: [...(form.images || []), dataUrl] });
      haptic('success');
    } catch (err) {
      console.error(err);
      alert('画像のアップロードに失敗しました');
      haptic('error');
    } finally {
      setSlotImageUploading(false);
      if (slotImageInputRef.current) slotImageInputRef.current.value = '';
    }
  }

  function removeSlotImage(idx) {
    setForm({ ...form, images: (form.images || []).filter((_, i) => i !== idx) });
  }

  function moveSlotImage(idx, direction) {
    const imgs = [...(form.images || [])];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= imgs.length) return;
    [imgs[idx], imgs[newIdx]] = [imgs[newIdx], imgs[idx]];
    setForm({ ...form, images: imgs });
  }

  async function remove(id) {
    if (!confirm('この枠を削除しますか?')) return;
    const next = (slots || []).filter((s) => s.id !== id);
    await onSave(next);
    haptic('warning');
  }

  async function handleAdUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, 1024, 0.85);
      // 新形式: オブジェクトとして追加（slot_idsは空）
      const newAd = { data_url: dataUrl, slot_ids: [] };
      // 既存の広告も全部正規化
      const normalized = (ads || []).map((a) =>
        typeof a === 'string' ? { data_url: a, slot_ids: [] } : { data_url: a.data_url, slot_ids: a.slot_ids || [] }
      );
      await onAdsChange([newAd, ...normalized].slice(0, 5));
      haptic('success');
    } catch (err) {
      console.error(err);
      alert('画像のアップロードに失敗しました');
      haptic('error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function removeAd(idx) {
    const next = (ads || []).filter((_, i) => i !== idx);
    await onAdsChange(next);
  }

  // 広告と予約枠の関連付けを更新
  async function updateAdSlots(idx, newSlotIds) {
    const next = (ads || []).map((a, i) => {
      if (i !== idx) {
        // 他の広告も新形式に正規化
        return typeof a === 'string' ? { data_url: a, slot_ids: [] } : a;
      }
      const dataUrl = typeof a === 'string' ? a : a.data_url;
      return { data_url: dataUrl, slot_ids: newSlotIds };
    });
    await onAdsChange(next);
    haptic('success');
  }

  // 関連枠選択モーダルの状態
  const [linkEditingIdx, setLinkEditingIdx] = useState(null); // 現在編集中の広告index

  const sortedSlots = [...(slots || [])].sort((a, b) =>
    ((a.date || '') + (a.time || '')).localeCompare((b.date || '') + (b.time || ''))
  );

  return (
    <div style={{ padding: 16, maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 }}>枠管理</h2>

      <div style={{ ...sty.cardLg, marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 'bold', color: '#475569', marginTop: 0, marginBottom: 12 }}>
          広告画像 ({(ads || []).length}/5)
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {(ads || []).map((ad, idx) => {
            const dataUrl = typeof ad === 'string' ? ad : ad.data_url;
            const slotIds = (typeof ad === 'string' ? [] : (ad.slot_ids || []));
            const linkedCount = slotIds.length;
            return (
              <div key={idx} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: 10, background: '#f8fafc', borderRadius: 10,
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ position: 'relative', width: 70, height: 70, flexShrink: 0 }}>
                  <img src={dataUrl} alt="" style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    borderRadius: 6, background: '#f1f5f9',
                  }} />
                  <button onClick={() => removeAd(idx)} style={{
                    position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                    background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                  }}>×</button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                    広告 {idx + 1}
                  </div>
                  {linkedCount > 0 ? (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, color: '#0f766e', background: '#ccfbf1',
                      padding: '3px 8px', borderRadius: 4, marginBottom: 6,
                      fontWeight: 'bold',
                    }}>
                      🔗 {linkedCount}件のイベントに関連付け
                    </div>
                  ) : (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, color: '#94a3b8', background: '#f1f5f9',
                      padding: '3px 8px', borderRadius: 4, marginBottom: 6,
                    }}>
                      関連付けなし
                    </div>
                  )}
                  <button onClick={() => setLinkEditingIdx(idx)} style={{
                    display: 'block', padding: '6px 10px', fontSize: 11,
                    background: 'white', color: TIFFANY,
                    border: `1px solid ${TIFFANY}`, borderRadius: 6,
                    cursor: 'pointer', fontWeight: 'bold',
                  }}>
                    {linkedCount > 0 ? '関連イベントを編集' : 'イベントを関連付ける'}
                  </button>
                </div>
              </div>
            );
          })}
          {(ads || []).length < 5 && (
            <label style={{
              padding: 16, border: '2px dashed #cbd5e1', borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#94a3b8', gap: 8,
              background: '#f8fafc',
            }}>
              {uploading ? (
                <span style={{ fontSize: 13 }}>アップロード中...</span>
              ) : (
                <>
                  <IconImage size={20} color="#94a3b8" />
                  <span style={{ fontSize: 13 }}>新しい広告画像を追加</span>
                </>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAdUpload}
                style={{ display: 'none' }} disabled={uploading} />
            </label>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          オススメ予約タブの上部に表示されます。関連付けすると画像タップでイベント一覧が出ます。
        </div>
      </div>

      {/* 関連枠選択モーダル */}
      {linkEditingIdx !== null && (ads || [])[linkEditingIdx] !== undefined && (
        <AdSlotPicker
          ad={(ads || [])[linkEditingIdx]}
          slots={slots}
          onClose={() => setLinkEditingIdx(null)}
          onSave={async (newSlotIds) => {
            await updateAdSlots(linkEditingIdx, newSlotIds);
            setLinkEditingIdx(null);
          }}
        />
      )}

      {!editing && (
        <button onClick={startNew} style={{ ...sty.btn, marginBottom: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: 'auto', padding: '10px 20px' }}>
          <IconPlus size={16} color="white" /> 枠を追加
        </button>
      )}

      {editing && (
        <div style={{ ...sty.cardLg }}>
          <h3 style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b', marginTop: 0 }}>
            {editing === 'new' ? '新規枠' : '枠を編集'}
          </h3>
          <Field label="日付" required>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={sty.input} />
          </Field>
          <Field label="開始時刻" required>
            <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} style={sty.input} />
          </Field>
          <Field label="種別">
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={sty.input} />
          </Field>
          <Field label="定員">
            <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} style={sty.input} min="1" />
          </Field>
          <Field label="ダミー予約数 (集客見せ用)">
            <input type="number" value={form.dummy_reserved} onChange={(e) => setForm({ ...form, dummy_reserved: e.target.value })} style={sty.input} min="0" />
          </Field>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.recommended} onChange={(e) => setForm({ ...form, recommended: e.target.checked })} />
              <span style={{ fontSize: 14 }}>★ オススメ予約として表示</span>
            </label>
          </Field>

          {/* イベント画像 (最大3枚) */}
          <Field label={`イベント画像 (${(form.images || []).length}/3)`}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {(form.images || []).map((img, idx) => (
                <div key={idx} style={{ position: 'relative', width: 90, height: 90 }}>
                  <img src={img} alt="" style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    borderRadius: 8, border: '1px solid #e2e8f0',
                  }} />
                  {/* 順序ラベル */}
                  <div style={{
                    position: 'absolute', top: 4, left: 4,
                    background: 'rgba(0,0,0,0.6)', color: 'white',
                    padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 'bold',
                  }}>{idx + 1}</div>
                  {/* 削除ボタン */}
                  <button onClick={() => removeSlotImage(idx)} style={{
                    position: 'absolute', top: -6, right: -6, width: 22, height: 22,
                    background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                  }}>×</button>
                  {/* 並び替えボタン */}
                  <div style={{
                    position: 'absolute', bottom: 4, left: 4, right: 4,
                    display: 'flex', justifyContent: 'space-between', gap: 4,
                  }}>
                    {idx > 0 && (
                      <button onClick={() => moveSlotImage(idx, -1)} style={{
                        flex: 1, padding: '2px 0', fontSize: 10,
                        background: 'rgba(0,0,0,0.6)', color: 'white',
                        border: 'none', borderRadius: 3, cursor: 'pointer',
                      }}>←</button>
                    )}
                    {idx < (form.images || []).length - 1 && (
                      <button onClick={() => moveSlotImage(idx, 1)} style={{
                        flex: 1, padding: '2px 0', fontSize: 10,
                        background: 'rgba(0,0,0,0.6)', color: 'white',
                        border: 'none', borderRadius: 3, cursor: 'pointer',
                      }}>→</button>
                    )}
                  </div>
                </div>
              ))}
              {(form.images || []).length < 3 && (
                <label style={{
                  width: 90, height: 90, border: '2px dashed #cbd5e1', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  color: '#94a3b8', flexDirection: 'column', gap: 4,
                  background: '#f8fafc',
                }}>
                  {slotImageUploading ? (
                    <span style={{ fontSize: 11 }}>アップ中...</span>
                  ) : (
                    <>
                      <IconImage size={20} color="#94a3b8" />
                      <span style={{ fontSize: 10 }}>画像を追加</span>
                    </>
                  )}
                  <input ref={slotImageInputRef} type="file" accept="image/*"
                    onChange={handleSlotImageUpload} style={{ display: 'none' }}
                    disabled={slotImageUploading} />
                </label>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              ←→ボタンで順序変更 / 詳細画面でスワイプ表示されます
            </div>
          </Field>

          {/* 詳細説明 */}
          <Field label="イベント詳細説明">
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={'例)\n🏆 優勝賞金 30,000円\n💰 参加費 3,000円\n👩 女性割引 -1,000円\n🔰 初心者歓迎\n\n※フリースタックトーナメント'}
              rows={8}
              style={{
                ...sty.input,
                fontFamily: 'inherit',
                resize: 'vertical',
                lineHeight: 1.6,
                minHeight: 120,
              }}
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              改行・絵文字OK / 「詳しく見る」をタップしたお客様に表示されます
            </div>
          </Field>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} style={{ ...sty.btn, flex: 1 }}>保存</button>
            <button onClick={() => setEditing(null)} style={sty.btnSec}>キャンセル</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {sortedSlots.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
            枠がまだありません
          </div>
        ) : (
          sortedSlots.map((slot) => {
            const hasImages = Array.isArray(slot.images) && slot.images.length > 0;
            const hasDesc = !!slot.description;
            return (
            <div key={slot.id} style={sty.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                    {slot.recommended && <span style={{ color: '#f59e0b' }}>★ </span>}
                    {formatDateFull(slot.date)} {slot.time}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {slot.title} / 定員{slot.capacity} / ダミー{slot.dummy_reserved || 0}
                  </div>
                  {(hasImages || hasDesc) && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      {hasImages && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px',
                          background: '#dbeafe', color: '#1e40af',
                          borderRadius: 3, fontWeight: 'bold',
                        }}>📷 {slot.images.length}枚</span>
                      )}
                      {hasDesc && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px',
                          background: '#d1fae5', color: '#065f46',
                          borderRadius: 3, fontWeight: 'bold',
                        }}>📝 詳細あり</span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => startEdit(slot)} style={{
                    padding: 8, background: 'transparent', border: '1px solid #cbd5e1',
                    borderRadius: 6, cursor: 'pointer',
                  }}><IconEdit size={14} color="#64748b" /></button>
                  <button onClick={() => remove(slot.id)} style={{
                    padding: 8, background: 'transparent', border: '1px solid #fca5a5',
                    borderRadius: 6, cursor: 'pointer',
                  }}><IconTrash size={14} color="#dc2626" /></button>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}


// ============================================================
// AdminHoursView (営業時間管理)
// ============================================================
function AdminHoursView({ hours, onSave }) {
  const [draft, setDraft] = useState(hours || {});
  const [days] = useState(30);

  useEffect(() => { setDraft(hours || {}); }, [hours]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dates = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    dates.push(toLocalDateStr(d));
  }

  function update(date, field, value) {
    setDraft({
      ...draft,
      [date]: { ...(draft[date] || {}), [field]: value, date },
    });
  }

  async function commit() {
    await onSave(draft);
    haptic('success');
    alert('保存しました');
  }

  return (
    <div style={{ padding: 16, maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 }}>営業時間管理</h2>
      <button onClick={commit} style={{ ...sty.btn, width: 'auto', padding: '10px 20px', marginBottom: 16 }}>
        変更を保存
      </button>
      <div>
        {dates.map((date) => {
          const h = draft[date] || {};
          const dow = new Date(date + 'T00:00:00').getDay();
          const color = getDateColor(date);
          return (
            <div key={date} style={sty.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>
                  {formatDateFull(date)}
                </div>
                {isHoliday(date) && (
                  <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4 }}>祝</span>
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input type="checkbox" checked={h.closed === true}
                  onChange={(e) => update(date, 'closed', e.target.checked)} />
                <span style={{ fontSize: 13 }}>定休日</span>
              </label>
              {!h.closed && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="time" value={h.open_time || '11:00'} onChange={(e) => update(date, 'open_time', e.target.value)} style={{ ...sty.input, width: 'auto' }} />
                  <span>〜</span>
                  <input type="time" value={h.close_time || '23:00'} onChange={(e) => update(date, 'close_time', e.target.value)} style={{ ...sty.input, width: 'auto' }} />
                </div>
              )}
              <input type="text" value={h.note || ''} onChange={(e) => update(date, 'note', e.target.value)}
                placeholder="備考 (任意)" style={{ ...sty.input, marginTop: 8 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// AdminReservationsView (予約一覧 - 個別削除機能つき)
// ============================================================
function AdminReservationsView({ reservations, slots, profiles, onDeleteReservation }) {
  const [filter, setFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState('all');

  const slotMap = {};
  for (const s of (slots || [])) { if (s && s.id) slotMap[s.id] = s; }
  const profileMap = {};
  for (const p of (profiles || [])) { if (p && p.id) profileMap[p.id] = p; }

  const filtered = (reservations || []).filter((r) => {
    if (!r) return false;
    if (slotFilter !== 'all' && r.slot_id !== slotFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return (r.name || '').toLowerCase().includes(q)
        || (r.musashi_id || '').toLowerCase().includes(q)
        || (r.phone || '').includes(q);
    }
    return true;
  }).sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

  function exportCSV() {
    const rows = [['予約日時', '日程', '時刻', '種別', 'お名前', 'ムサシID', 'メール', '電話番号']];
    for (const r of filtered) {
      const s = slotMap[r.slot_id] || {};
      const p = profileMap[r.user_id] || {};
      rows.push([
        r.created_at ? new Date(r.created_at).toLocaleString('ja-JP') : '',
        s.date || '', s.time || '', s.title || '',
        r.name || '', r.musashi_id || '',
        p.email || '',
        r.phone || p.phone || '',
      ]);
    }
    const csv = '\uFEFF' + rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservations_${toLocalDateStr()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    haptic('success');
  }

  async function handleDelete(r) {
    const s = slotMap[r.slot_id] || {};
    if (!confirm(`「${r.name}様」の予約 (${formatDateFull(s.date)} ${s.time}) を削除しますか？\nこの操作は取り消せません。`)) return;
    try {
      await onDeleteReservation(r.id);
      haptic('warning');
    } catch (e) {
      haptic('error');
      alert('削除に失敗しました: ' + (e.message || ''));
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 }}>予約一覧</h2>

      <div style={{ ...sty.card, marginBottom: 16 }}>
        <Field label="検索 (名前/ID/電話番号)">
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="検索キーワード" style={sty.input} />
        </Field>
        <Field label="枠で絞り込み">
          <select value={slotFilter} onChange={(e) => setSlotFilter(e.target.value)} style={sty.input}>
            <option value="all">すべて</option>
            {(slots || []).map((s) => (
              <option key={s.id} value={s.id}>
                {formatDateFull(s.date)} {s.time} {s.title}
              </option>
            ))}
          </select>
        </Field>
        <button onClick={exportCSV} style={{ ...sty.btn, marginTop: 8 }}>
          CSV出力 ({filtered.length}件)
        </button>
      </div>

      <div>
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>該当する予約がありません</div>
        ) : (
          filtered.map((r) => {
            const s = slotMap[r.slot_id] || {};
            return (
              <div key={r.id} style={sty.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString('ja-JP') : ''}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  ID: {r.musashi_id} {(r.phone || profileMap[r.user_id]?.phone) && ` / ☎ ${r.phone || profileMap[r.user_id]?.phone}`}
                </div>
                {profileMap[r.user_id]?.email && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    📧 {profileMap[r.user_id].email}
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9',
                }}>
                  <div style={{ fontSize: 12, color: TIFFANY, fontWeight: 'bold' }}>
                    {formatDateFull(s.date)} {s.time} {s.title}
                  </div>
                  <button onClick={() => handleDelete(r)} style={{
                    padding: '6px 10px', background: 'transparent',
                    color: '#dc2626', border: '1px solid #fca5a5',
                    borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    <IconTrash size={12} color="#dc2626" />
                    削除
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


// ============================================================
// AdminCustomersView (顧客管理 - NEW!)
// ============================================================
function AdminCustomersView({ profiles, reservations, slots, admins }) {
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const slotMap = {};
  for (const s of (slots || [])) { if (s && s.id) slotMap[s.id] = s; }
  const adminUserIds = new Set((admins || []).map(a => a.user_id));

  // 各会員の予約数を計算
  const reservationCountByUser = {};
  for (const r of (reservations || [])) {
    if (r && r.user_id) {
      reservationCountByUser[r.user_id] = (reservationCountByUser[r.user_id] || 0) + 1;
    }
  }

  // 検索フィルタリング
  const filteredProfiles = (profiles || []).filter((p) => {
    if (!p) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (p.name || '').toLowerCase().includes(q)
      || (p.musashi_id || '').toLowerCase().includes(q)
      || (p.email || '').toLowerCase().includes(q)
      || (p.phone || '').includes(q);
  }).sort((a, b) => {
    // 予約数が多い順
    const ca = reservationCountByUser[a.id] || 0;
    const cb = reservationCountByUser[b.id] || 0;
    if (cb !== ca) return cb - ca;
    return (a.name || '').localeCompare(b.name || '');
  });

  // 詳細画面
  if (selected) {
    const profile = (profiles || []).find(p => p.id === selected);
    if (!profile) {
      setSelected(null);
      return null;
    }
    const myRes = (reservations || [])
      .filter(r => r && r.user_id === profile.id)
      .sort((a, b) => {
        const sa = slotMap[a.slot_id] || {};
        const sb = slotMap[b.slot_id] || {};
        return ((sb.date || '') + (sb.time || '')).localeCompare((sa.date || '') + (sa.time || ''));
      });
    const todayStr = toLocalDateStr();
    const upcomingRes = myRes.filter(r => {
      const s = slotMap[r.slot_id];
      return s && s.date >= todayStr;
    });
    const pastRes = myRes.filter(r => {
      const s = slotMap[r.slot_id];
      return s && s.date < todayStr;
    });
    const isAdmin = adminUserIds.has(profile.id);

    return (
      <div style={{ padding: 16, maxWidth: 600, margin: '0 auto' }}>
        <button onClick={() => setSelected(null)} style={sty.btnGhost}>
          <IconChevronLeft size={16} /> 顧客一覧に戻る
        </button>

        <div style={{ ...sty.cardLg, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: TIFFANY,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 22, fontWeight: 'bold',
            }}>
              {(profile.name || '?').charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1e293b' }}>
                {profile.name}
                {isAdmin && (
                  <span style={{
                    marginLeft: 6, fontSize: 10, padding: '2px 6px',
                    background: '#fef3c7', color: '#92400e', borderRadius: 4,
                    display: 'inline-flex', alignItems: 'center', gap: 2, verticalAlign: 'middle',
                  }}>
                    <IconShield size={10} color="#92400e" />管理者
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>ID: {profile.musashi_id}</div>
            </div>
          </div>

          <Row label="メール" value={profile.email || '-'} />
          <Row label="電話番号" value={profile.phone || '-'} />
          <Row label="登録日" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString('ja-JP') : '-'} />
          <Row label="予約数(全期間)" value={`${myRes.length}件`} />
          <Row label="今後の予約" value={`${upcomingRes.length}件`} />
          <Row label="過去の予約" value={`${pastRes.length}件`} />
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 'bold', color: '#1e293b', margin: '20px 0 12px' }}>
          予約履歴
        </h3>

        {myRes.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
            この会員の予約はありません
          </div>
        ) : (
          myRes.map(r => {
            const s = slotMap[r.slot_id] || {};
            const isPast = s.date && s.date < todayStr;
            return (
              <div key={r.id} style={{
                ...sty.card,
                opacity: isPast ? 0.6 : 1,
                borderLeft: `4px solid ${isPast ? '#cbd5e1' : TIFFANY}`,
              }}>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>
                  {formatDateFull(s.date)} {s.time}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {s.title || ''}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                  {isPast ? '完了' : '予約中'} / 予約日時: {r.created_at ? new Date(r.created_at).toLocaleString('ja-JP') : '-'}
                </div>
              </div>
            );
          })
        )}

        <div style={{
          marginTop: 24, padding: 12, background: '#f0f9ff',
          border: '1px solid #bae6fd', borderRadius: 8,
          fontSize: 12, color: '#0369a1',
        }}>
          💡 顧客アカウントの削除や管理者権限の付与/剥奪は、Supabaseの管理画面から直接行ってください。
        </div>
      </div>
    );
  }

  // 一覧画面
  return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginBottom: 4 }}>顧客管理</h2>
      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
        会員登録された方の一覧です（{filteredProfiles.length}名）
      </p>

      <div style={{ ...sty.card, marginBottom: 16 }}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
            <IconSearch size={16} color="#94a3b8" />
          </div>
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="名前・ID・メール・電話番号で検索"
            style={{ ...sty.input, paddingLeft: 36 }} />
        </div>
      </div>

      {filteredProfiles.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <IconUsers size={32} color="#cbd5e1" />
          <div style={{ marginTop: 8 }}>
            {filter ? '該当する会員がいません' : '会員がまだいません'}
          </div>
        </div>
      ) : (
        filteredProfiles.map((p) => {
          const resCount = reservationCountByUser[p.id] || 0;
          const isAdmin = adminUserIds.has(p.id);
          return (
            <div key={p.id} onClick={() => { haptic('light'); setSelected(p.id); }} style={{
              ...sty.card, cursor: 'pointer',
              transition: 'transform 0.1s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', background: TIFFANY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 16, fontWeight: 'bold', flexShrink: 0,
                }}>
                  {(p.name || '?').charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1e293b' }}>
                      {p.name || '(名前未設定)'}
                    </div>
                    {isAdmin && (
                      <span style={{
                        fontSize: 9, padding: '1px 5px',
                        background: '#fef3c7', color: '#92400e', borderRadius: 3,
                        display: 'inline-flex', alignItems: 'center', gap: 2,
                      }}>
                        <IconShield size={9} color="#92400e" />管理者
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    ID: {p.musashi_id} {p.phone && ` / ☎ ${p.phone}`}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                    📧 {p.email}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: resCount > 0 ? TIFFANY : '#cbd5e1' }}>
                    {resCount}
                  </div>
                  <div style={{ fontSize: 9, color: '#94a3b8' }}>予約</div>
                </div>
                <IconChevronRight size={16} color="#cbd5e1" />
              </div>
            </div>
          );
        })
      )}

      <div style={{
        marginTop: 16, padding: 12, background: '#f0f9ff',
        border: '1px solid #bae6fd', borderRadius: 8,
        fontSize: 11, color: '#0369a1',
      }}>
        💡 会員の詳細を見るにはタップしてください。<br/>
        会員アカウントの削除はSupabaseの管理画面から直接行ってください。
      </div>
    </div>
  );
}


// ============================================================
// メインコンポーネント (Supabase版)
// ============================================================
function MusashiPokerReservationInner() {
  const isAdminUrl = typeof window !== 'undefined'
    && window.location && window.location.pathname
    && window.location.pathname.indexOf('/admin') === 0;

  const isPasswordReset = typeof window !== 'undefined'
    && (window.location.hash.includes('type=recovery')
        || window.location.search.includes('reset=true'));

  const [view, setView] = useState(isAdminUrl ? 'admin-slots' : 'reserve');
  const [authView, setAuthView] = useState(isPasswordReset ? 'password-update' : 'login');
  const [slots, setSlots] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [hours, setHours] = useState({});
  const [ads, setAds] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [lastReserveTab, setLastReserveTab] = useState('recommend');
  const [customerResetKey, setCustomerResetKey] = useState(0);

  if (!SUPABASE_CONFIGURED) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fff', padding: 20,
      }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <h2 style={{ color: '#dc2626' }}>Supabase設定が必要です</h2>
          <p style={{ color: '#475569' }}>
            VercelでSupabaseの環境変数を設定してください:<br/>
            <code>VITE_SUPABASE_URL</code> と <code>VITE_SUPABASE_KEY</code>
          </p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!cancelled) setSession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (cancelled) return;
      setSession(sess);
      if (_event === 'PASSWORD_RECOVERY') {
        setAuthView('password-update');
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session || !session.user) {
      setProfile(null);
      setIsAdmin(false);
      return;
    }
    setIsAdmin(null);
    let cancelled = false;
    (async () => {
      try {
        const { data: prof, error: profErr } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profErr && profErr.code !== 'PGRST116') {
          console.warn('profile load error:', profErr);
        }

        if (!prof && session.user.user_metadata) {
          const meta = session.user.user_metadata;
          if (meta.musashi_id) {
            const { data: created, error: createErr } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                name: meta.name || session.user.email,
                musashi_id: meta.musashi_id,
                email: session.user.email,
                phone: meta.phone || null,
              })
              .select()
              .single();
            if (createErr) {
              console.warn('profile create error:', createErr);
            } else if (!cancelled) {
              setProfile(created);
            }
          }
        } else if (!cancelled) {
          setProfile(prof);
        }

        const { data: adminCheck } = await supabase
          .from('admins')
          .select('user_id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (!cancelled) setIsAdmin(!!adminCheck);
      } catch (e) {
        console.error('session load:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [slotsRes, hoursRes, adsRes, resRes] = await Promise.all([
          supabase.from('slots').select('*').order('date').order('time'),
          supabase.from('hours').select('*'),
          supabase.from('ads').select('*').order('position'),
          supabase.from('reservations').select('*'),
        ]);

        if (cancelled) return;

        if (slotsRes.error) console.warn('slots load:', slotsRes.error);
        else setSlots(slotsRes.data || []);

        if (hoursRes.error) console.warn('hours load:', hoursRes.error);
        else {
          const hoursMap = {};
          for (const h of (hoursRes.data || [])) {
            hoursMap[h.date] = h;
          }
          setHours(hoursMap);
        }

        if (adsRes.error) console.warn('ads load:', adsRes.error);
        else setAds((adsRes.data || []).map((a) => ({
          data_url: a.data_url,
          slot_ids: Array.isArray(a.slot_ids) ? a.slot_ids : [],
        })));

        if (resRes.error) console.warn('reservations load:', resRes.error);
        else setReservations(resRes.data || []);
      } catch (e) {
        console.error('data load:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 管理者がログインしたら、profilesとadminsも取得
  useEffect(() => {
    if (isAdmin !== true) { setProfiles([]); setAdmins([]); return; }
    let cancelled = false;
    (async () => {
      const [profilesRes, adminsRes] = await Promise.all([
        supabase.from('profiles').select('*'),
        supabase.from('admins').select('*'),
      ]);
      if (!cancelled) {
        if (profilesRes.error) console.warn(profilesRes.error);
        else setProfiles(profilesRes.data || []);
        if (adminsRes.error) console.warn(adminsRes.error);
        else setAdmins(adminsRes.data || []);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  async function saveSlots(nextSlots) {
    const isExistingId = (id) => {
      if (!id) return false;
      const s = String(id);
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    };

    const existing = nextSlots.filter((s) => isExistingId(s.id));
    const created = nextSlots.filter((s) => !isExistingId(s.id));
    const currentIds = new Set(existing.map((s) => s.id));
    const oldIds = (slots || []).map((s) => s.id).filter((id) => isExistingId(id) && !currentIds.has(id));

    try {
      if (oldIds.length > 0) {
        await supabase.from('slots').delete().in('id', oldIds);
      }
      for (const s of existing) {
        await supabase.from('slots').update({
          date: s.date,
          time: s.time,
          capacity: Number(s.capacity) || 0,
          title: s.title || null,
          recommended: !!s.recommended,
          dummy_reserved: Number(s.dummy_reserved) || 0,
          description: s.description || null,
          images: Array.isArray(s.images) ? s.images : [],
        }).eq('id', s.id);
      }
      for (const s of created) {
        await supabase.from('slots').insert({
          date: s.date,
          time: s.time,
          capacity: Number(s.capacity) || 0,
          title: s.title || null,
          recommended: !!s.recommended,
          dummy_reserved: Number(s.dummy_reserved) || 0,
          description: s.description || null,
          images: Array.isArray(s.images) ? s.images : [],
        });
      }
      const { data } = await supabase.from('slots').select('*').order('date').order('time');
      setSlots(data || []);
    } catch (e) {
      console.error('saveSlots:', e);
      throw e;
    }
  }

  async function saveHours(nextHours) {
    try {
      const upserts = [];
      for (const date of Object.keys(nextHours)) {
        const h = nextHours[date];
        if (!h) continue;
        upserts.push({
          date,
          closed: h.closed === true,
          open_time: h.open_time || null,
          close_time: h.close_time || null,
          note: h.note || null,
        });
      }
      if (upserts.length > 0) {
        await supabase.from('hours').upsert(upserts, { onConflict: 'date' });
      }
      const { data } = await supabase.from('hours').select('*');
      const map = {};
      for (const h of (data || [])) map[h.date] = h;
      setHours(map);
    } catch (e) {
      console.error('saveHours:', e);
      throw e;
    }
  }

  async function saveAds(nextAds) {
    try {
      await supabase.from('ads').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (nextAds && nextAds.length > 0) {
        const records = nextAds.map((ad, idx) => {
          // 後方互換: 文字列ならURLのみ、オブジェクトなら slot_ids も含む
          if (typeof ad === 'string') {
            return { data_url: ad, position: idx, slot_ids: [] };
          }
          return {
            data_url: ad.data_url,
            position: idx,
            slot_ids: Array.isArray(ad.slot_ids) ? ad.slot_ids : [],
          };
        });
        await supabase.from('ads').insert(records);
      }
      // 内部状態は常にオブジェクト形式で保持
      const normalized = (nextAds || []).map((ad) => {
        if (typeof ad === 'string') return { data_url: ad, slot_ids: [] };
        return { data_url: ad.data_url, slot_ids: ad.slot_ids || [] };
      });
      setAds(normalized);
    } catch (e) {
      console.error('saveAds:', e);
      throw e;
    }
  }

  async function addReservation(data) {
    const dup = (reservations || []).find(
      (r) => r && r.user_id === data.user_id && r.slot_id === data.slot_id
    );
    if (dup) {
      throw new Error('この予約枠は既に予約済みです');
    }
    const { data: created, error } = await supabase
      .from('reservations')
      .insert(data)
      .select()
      .single();
    if (error) {
      console.error('addReservation:', error);
      throw error;
    }
    setReservations([...reservations, created]);
    return created;
  }

  async function cancelReservation(resId) {
    const { error } = await supabase.from('reservations').delete().eq('id', resId);
    if (error) {
      console.error('cancelReservation:', error);
      throw error;
    }
    setReservations(reservations.filter((r) => r.id !== resId));
  }

  async function updateProfile(updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', session.user.id)
      .select()
      .single();
    if (error) {
      console.error('updateProfile:', error);
      throw error;
    }
    setProfile(data);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setAuthView('login');
  }

  // ロゴクリック時のハンドラ
  function handleLogoClick() {
    haptic('light');
    if (isAdminUrl) {
      // 管理画面の場合、トップ（枠管理）へ
      setView('admin-slots');
    } else if (session && profile) {
      // ログイン中の場合、予約画面へ＆CustomerViewの内部状態もリセット
      setView('reserve');
      setCustomerResetKey((k) => k + 1);
    } else {
      // 未ログインの場合、ログイン画面へ
      setAuthView('login');
    }
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(180deg, #F0F7FB 0%, #DCEAF5 100%)',
        flexDirection: 'column', gap: 16,
      }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid rgba(10,186,181,0.2)',
          borderTopColor: TIFFANY, borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ color: '#64748b', letterSpacing: '0.2em', fontSize: 12 }}>LOADING...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (showSplash && !isAdminUrl && !isPasswordReset) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  if (isPasswordReset && session) {
    return (
      <div style={sty.page}>
        <header style={sty.header}>
          <ClickableLogo onClick={handleLogoClick} />
        </header>
        <PasswordUpdateView onComplete={() => {
          window.history.replaceState(null, '', window.location.pathname);
          setAuthView('login');
        }} />
      </div>
    );
  }

  if (isAdminUrl) {
    if (!session) {
      return (
        <div style={sty.page}>
          <header style={sty.header}>
            <ClickableLogo onClick={handleLogoClick} />
          </header>
          <LoginView
            onSignupClick={() => alert('管理者アカウントは管理者にお問い合わせください')}
            onResetClick={() => setAuthView('reset')}
          />
        </div>
      );
    }
    if (isAdmin === null) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(180deg, #F0F7FB 0%, #DCEAF5 100%)',
          flexDirection: 'column', gap: 16,
        }}>
          <div style={{
            width: 36, height: 36,
            border: '3px solid rgba(10,186,181,0.2)',
            borderTopColor: TIFFANY, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <div style={{ color: '#64748b', letterSpacing: '0.2em', fontSize: 12 }}>CHECKING...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }
    if (isAdmin === false) {
      return (
        <div style={sty.page}>
          <header style={sty.header}>
            <ClickableLogo onClick={handleLogoClick} />
          </header>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <h2 style={{ color: '#dc2626' }}>権限がありません</h2>
            <p style={{ color: '#64748b' }}>このアカウントは管理者ではありません</p>
            <button onClick={handleLogout} style={{ ...sty.btnSec, marginTop: 16 }}>
              ログアウト
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={sty.page}>
        <header style={sty.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ClickableLogo onClick={handleLogoClick} />
            <div style={{
              fontSize: 11, padding: '2px 8px', background: TIFFANY, color: 'white',
              borderRadius: 4, fontWeight: 'bold',
            }}>ADMIN</div>
          </div>
          <button onClick={handleLogout} style={{
            padding: '6px 12px', fontSize: 13,
            background: 'transparent', color: '#dc2626',
            border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer',
          }}>ログアウト</button>
        </header>
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
          <NavBtn active={view === 'admin-slots'} icon={<IconCalendar size={18} color={view === 'admin-slots' ? TIFFANY : '#94a3b8'} />} label="枠管理" onClick={() => setView('admin-slots')} />
          <NavBtn active={view === 'admin-hours'} icon={<IconClock size={18} color={view === 'admin-hours' ? TIFFANY : '#94a3b8'} />} label="営業時間" onClick={() => setView('admin-hours')} />
          <NavBtn active={view === 'admin-reservations'} icon={<IconCalendar size={18} color={view === 'admin-reservations' ? TIFFANY : '#94a3b8'} />} label="予約一覧" onClick={() => setView('admin-reservations')} />
          <NavBtn active={view === 'admin-customers'} icon={<IconUsers size={18} color={view === 'admin-customers' ? TIFFANY : '#94a3b8'} />} label="顧客管理" onClick={() => setView('admin-customers')} />
        </div>
        {view === 'admin-slots' && <AdminSlotsView slots={slots} onSave={saveSlots} ads={ads} onAdsChange={saveAds} />}
        {view === 'admin-hours' && <AdminHoursView hours={hours} onSave={saveHours} />}
        {view === 'admin-reservations' && <AdminReservationsView reservations={reservations} slots={slots} profiles={profiles} onDeleteReservation={cancelReservation} />}
        {view === 'admin-customers' && <AdminCustomersView profiles={profiles} reservations={reservations} slots={slots} admins={admins} />}
      </div>
    );
  }

  // お客様画面 - 未ログイン
  if (!session || !profile) {
    return (
      <div style={sty.page}>
        <header style={sty.header}>
          <ClickableLogo onClick={handleLogoClick} />
        </header>
        {authView === 'signup' && (
          <SignupView onLoginClick={() => setAuthView('login')} />
        )}
        {authView === 'reset' && (
          <PasswordResetView onLoginClick={() => setAuthView('login')} />
        )}
        {authView === 'login' && (
          <LoginView
            onSignupClick={() => setAuthView('signup')}
            onResetClick={() => setAuthView('reset')}
          />
        )}
      </div>
    );
  }

  // お客様画面 - ログイン後
  return (
    <div style={sty.page}>
      <header style={sty.header}>
        <ClickableLogo onClick={handleLogoClick} />
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {profile.name}さん
        </div>
      </header>
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
        <NavBtn active={view === 'reserve'} icon={<IconCalendar size={18} color={view === 'reserve' ? TIFFANY : '#94a3b8'} />} label="予約する" onClick={() => { setView('reserve'); setCustomerResetKey((k) => k + 1); }} />
        <NavBtn active={view === 'my-reservations'} icon={<IconStar size={18} color={view === 'my-reservations' ? TIFFANY : '#94a3b8'} />} label="マイ予約" onClick={() => setView('my-reservations')} />
        <NavBtn active={view === 'profile'} icon={<IconSettings size={18} color={view === 'profile' ? TIFFANY : '#94a3b8'} />} label="プロフィール" onClick={() => setView('profile')} />
      </div>
      {view === 'reserve' && (
        <CustomerView user={session.user} profile={profile}
          slots={slots} reservations={reservations}
          hours={hours} ads={ads} onReserve={addReservation}
          lastTab={lastReserveTab} onTabChange={setLastReserveTab}
          onGoToMyReservations={() => setView('my-reservations')}
          resetKey={customerResetKey} />
      )}
      {view === 'my-reservations' && (
        <MyReservationsView user={session.user} reservations={reservations}
          slots={slots} onCancel={cancelReservation} />
      )}
      {view === 'profile' && (
        <ProfileView user={session.user} profile={profile}
          onUpdate={updateProfile} onLogout={handleLogout} />
      )}
    </div>
  );
}

// ============================================================
// エクスポート
// ============================================================
export default function MusashiPokerReservation() {
  return (
    <ErrorBoundary>
      <MusashiPokerReservationInner />
    </ErrorBoundary>
  );
}
